/**
 * Export handlers for the in-season screens (G2.4, G2 test d).
 *
 * One per data route. Each carries all nine provenance fields, which the type makes
 * non-optional (G2 test e), and numbers leave as `raw` — there is no path in
 * `ExportCell` that carries a display string for a number, so "full precision,
 * unrounded, never a display string" is a type property rather than a discipline.
 *
 * **An export reads the same source the screen does.** G2.4's fidelity rule is that an
 * export reflects the view the planner is looking at. A resolver that answered from a
 * fixture while the screen answered from the service would hand a planner a spreadsheet
 * that disagrees with the screen it was exported from — the worst of both, and worse than
 * either alone. So every resolver whose screen is wired calls the same operation through
 * the same transport (`collectPages` from `@/api`; `12` §2.4 control 2 forbids a
 * hand-rolled request, and this is not one). The scope is PASSED (`ExportScope`, ADR
 * 0017) and falls back to the URL, exactly as the screens read it — G2.2 makes the URL
 * the state, which is what makes that sound rather than a trick.
 *
 * **Fidelity is per VIEW, not per route.** The markdown Output route carried four panels — a
 * price path, a carrying-cost frontier, a batch of options and the run's price-action
 * queue — and one resolver that answered the queue whatever was on screen was not "the
 * view the planner is looking at" (`docs/adr/0058`). Since issue 226 each of those panels is
 * a page with its own export, each reading the operation its page reads.
 *
 * The landing bridge is the one resolver whose screen is still fixture-backed; it says so
 * in its perimeter and is named in the report.
 *
 * **The cover export keeps reqs/00 D8's three states.** A CSV rendering *not ranged* and
 * *counted zero* both as `0` destroys the distinction the whole chapter rests on, and a
 * replenishment computed from the flattened file would ship stock to a door that already
 * holds it.
 *
 * **The markdown export states its base and window in the provenance sheet.** reqs/04 §2
 * records the same dataset producing three different markdown rates depending on
 * clipping, window and denominator; a spreadsheet that left the base implicit would
 * reintroduce exactly that.
 */

import { callOperation, collectPages, collectFresh } from '@/api';
import {
  foundationsListExceptions,
  inseasonListProposals,
  listAccuracy,
  listD2Evidence,
  listOpenPositions,
  listPriceActions,
  listTransfers,
  transferExplanationsReport,
  transferReasonVocabulary,
  pricingReport,
  replenishmentReport,
  transfersReport,
  listWriteBack,
  elasticityModelVersion,
  listElasticityEstimates,
  markdownFrontier,
  markdownPath,
  listRunConfigurations,
  listRunConfigurationVersions,
  getRunConfigurationVersion,
  listStockPositions,
  runProvenance,
  salesHistory,
} from '@/api/generated';
import type {
  AssembledMarkdownPath, ElasticityEstimate, GateD2EvidenceRow, PriceActionSummary, ProposalSummary,
  PricingReportRow, ReplenishmentReportRow, SalesHistoryWeek, SimulationView, StockPositionSummary, TransferExplanationReportRow, TransferReasonOption, TransferReportRow, WriteBackState,
} from '@/api/generated';
import type { ExportScope } from '../manifest';
import { i18next } from '@/i18n';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion, isAbsent as isAbsentProvenance } from '@/export';
import type { ColumnGuideSource, ExportCell, ExportColumn, ExportRequest, Provenance, ProvenanceFilter } from '@/export';
import type { ChartExportMeta } from '@/charts';
import {
  SIZE_EXPORT_COLUMNS, SIZE_EXPORT_KEYS, passesFilters, rankedProvenanceFilters, rankRows, readRankedScope, resolveRank,
  PERSON_NAME_DISPLAY, decidedByExportCell, sizeExportCells, sizeName, type RankedScope, type RankFilter,
} from '@/planning';

import {
  UNCLASSIFIED_CONSTRAINT_KEY, UNCLASSIFIED_MODE_KEY, UNCLASSIFIED_PURPOSE_KEY, UNCLASSIFIED_STREAM_KEY, UNRECOGNISED_DELIVERY_KEY,
  toAccuracyViews, toConstraintKey, toD2MarketViews, toDeliveryStateKey, toExceptions, toHoldReasonKey, toMeasuresAbsentKey, toModeKey, toPriceActionRows,
  toPurposeKey, toStreamKey, toTransferRows,
  type TransferRow,
} from './api';
import { splitDroppedDoors } from './dropped-door';
import {
  droppedAbsentNotes, droppedFilters, droppedMeasures, exclusionFiltersOf, exclusionOf, retitled, withDroppedSheet,
} from './dropped-door-export';
import {
  DRIVER_COLUMNS, PROPOSAL_LABEL_COLUMNS, STANDARD_COLUMN, catalogueWords, driverCells, proposalLabelCells, standardCell,
} from './export-drivers';
import { dispositionStateKey } from './transfer-dispositions';
import {
  PROVENANCE_COLUMNS, readExportContext, simulationFilters, withRunProvenance, type CitedMeasure, type ExportContext, type Translate,
} from './export-provenance';
import { BATCH_LIMIT, refusalCode } from './markdown';
import { toTransferActionGroups } from './transfer-action-types';
import {
  TRANSFER_WHY_DECIDED_BY_COLUMN, TRANSFER_WHY_FIELDS, transferDecidedByCell, transferDecidersOf, transferWhyCells, withDecidedBy,
  type TransferDeciders,
} from './transfer-why-fields';
import { transferWhyWorkbook } from './transfer-why-workbook';
import {
  HELD_BACK_EXPORT_ID, HELD_BACK_REASONS, HELD_BACK_TITLE_KEY, heldBackSheet, heldBackTable, readHeldBackMoves, type HeldBackMoves,
} from './held-back-moves';
import {
  indexReplenishmentReport, indexTransfersReport, PROPOSAL_ATTRIBUTE_COLUMNS, proposalAttributeCells, proposalLineKey,
  TRANSFER_ATTRIBUTE_COLUMNS, transferAttributeCells, PRICING_ATTRIBUTE_COLUMNS, indexPricingReport, pricingAttributeCells,
} from './report-attributes';
import { nameExportCells, nameExportColumns, readRunNames, type RunNames } from './run-names';
import { PENDING, transferRanking } from './transfer-ranking';
import { LINES_SERVED, readDepartment, readDrill, readLinesNarrowing } from './drill';
import { compareParameters, MARKET_PARAMETER, RUN_CONFIGURATION_SURFACE } from './run-configurations';
import { readComparePicks, readConfigurationDrill } from './run-configuration-drill';
import { objectiveQuery, readOpenOption, readScope } from './scope';
import { coverageWords, lineCoverage } from './uncovered';
import { isStoreTotalPosition, STORE_TOTAL_KEY } from './stock-summary';
import { elasticityBasis, originalPriceBasis, shrinkageOf, type StepBases } from './price-basis';
import { roundingModeLabelKey } from '@/planning';
import { NO_RUN, readInSeasonRunParameters, withParametersInForce } from '@/planning';
import type { InSeasonScope } from './scope';
import { readSalesWindow, salesHistoryQuery, windowStatement, type WindowStatement } from './sales-history';
import { logRanking, readNeedsAttention } from './write-back-log-filters';
import { ALERT_NAME_EXPORT_COLUMNS, ALERT_NAME_EXPORT_KEYS, alertNameExportCells } from '@/planning';

/**
 * **The frontier IS an export now, and this note records why the refusal expired.**
 *
 * It used to read: "the margin-versus-cash frontier is deliberately NOT an export — its
 * only route is an unassembled calculator that asks the caller for all twenty of its
 * inputs, so no measured frontier exists to export, and the illustrative rows in
 * `fixtures.ts` sit on screen behind a notice." Every clause of that was true and the
 * first one stopped being true: `GET /inseason/markdown/frontier` assembles the problem
 * over `packages/planning/` exactly as `GET /markdown/path` does, so the frontier on
 * screen is a measured answer from a single producer, and a refusal that outlived its
 * reason is just a missing feature with a good story.
 *
 * What has NOT changed is that the export names no recommended point. §4.4 wants
 * competing scenarios, and a spreadsheet with a highlighted row would be the hidden
 * weighting the whole panel exists to prevent — arriving in the artefact that outlives
 * the screen, which is worse.
 */
/** The build that produced the file — `buildVersion` in `@/export`, `docs/adr/9007`. */
const PLATFORM_VERSION = buildVersion();

/**
 * The scope the export is bounded by, read the same way the screens read it.
 *
 * `ExportScope` carries the URL (ADR 0017), so a resolver no longer has to reach for
 * `window` — which is what makes these testable without a jsdom navigation and callable
 * from a worker. `window` stays as the fallback for the zero-arity call sites that
 * predate the widened contract.
 */
function scope(given?: ExportScope): InSeasonScope {
  if (given !== undefined) return readScope(given.search);
  return readScope(typeof window === 'undefined' ? '' : window.location.search);
}

/**
 * The perimeter's run filter.
 *
 * An export taken with no run in the URL records that fact rather than omitting the
 * filter: a provenance sheet whose filter list is silent about the perimeter is a sheet
 * that cannot be reproduced, and G9 is exactly about reproducibility.
 */
function runFilter(runId: string | null): readonly ProvenanceFilter[] {
  return runId === null
    ? [{ field: 'run_id', operator: 'unset', value: '' }]
    : [{ field: 'run_id', operator: 'equals', value: runId }];
}

/**
 * The nine fields, with the perimeter the caller actually used.
 *
 * `hierarchyNode`, `scenario` and `planVersionId` are EMPTY where the reads this chapter
 * makes do not publish them. Empty is the honest value: a fabricated plan version in a
 * provenance sheet is a citation that will be believed. `generatedBy` is likewise empty
 * of a principal because the front end holds no identity claim; the service does, and
 * populating it here would be inventing an author for an audit record.
 */
function provenance(
  filters: readonly ProvenanceFilter[],
  measures: readonly { measure: string; definition: string }[],
  registerVersion = '',
): Provenance {
  return {
    filters,
    hierarchyNode: NOT_PUBLISHED,
    timeWindow: NOT_PUBLISHED,
    scenario: NOT_PUBLISHED,
    planVersionId: NOT_PUBLISHED,
    measureDefinitions: measures.map((entry) => ({
      measure: entry.measure,
      definition: entry.definition,
      registerVersion,
    })),
    generatedAt: new Date().toISOString(),
    generatedBy: NO_PRINCIPAL,
    platformVersion: PLATFORM_VERSION,
  };
}

/** §4.2 — the proposal queue, with the constraint that bound each line. */
export async function proposalExport(): Promise<ExportRequest> {
  const { runId } = scope();
  const items = runId === null
    ? []
    : await collectPages(inseasonListProposals, { query: { run_id: runId } });
  return proposalRequest(items, runId);
}

/**
 * The proposal queue's sheet over rows already read — the route's export above, and the CSV
 * under a capped proposal table (`docs/adr/9008`), which hands in the rows it draws.
 *
 * Reads the run's provenance and the platform version first (`docs/adr/9120`), so every row
 * leaves with its run, tick and metric definitions. `proposalSheet` is the pure half.
 */
export async function proposalRequest(
  items: readonly ProposalSummary[], runId: string | null, source: LineSource = runLineSource(runId),
): Promise<ExportRequest> {
  const [context, report] = await Promise.all([source.context(), readReplenishmentReport(source)]);
  return proposalSheet(items, runId, context, translateNow, report);
}

/**
 * Where a decision file's provenance context and report attributes are read from (`docs/adr/6027`).
 *
 * The sheet builders below are pure over lines, a context and a report, so a live run's file and a
 * simulation's are the SAME builder over a different source — the columns, their order, the sheets
 * and the measure citations cannot drift apart because there is one copy of them. The lines
 * themselves are handed in by the caller (a table's rows, or a walk of the simulation's pages).
 * A source never rejects: an unread report leaves every attribute absent with `not_in_report`.
 */
export interface LineSource {
  /** The provenance context: the run as the directory lists it, or the simulation as the platform states it. */
  readonly context: () => Promise<ExportContext>;
  /** The replenishment report of the file's subject — every row, or none when it cannot be read. */
  readonly replenishmentReport: () => Promise<readonly ReplenishmentReportRow[]>;
  /** The pricing report of the file's subject. */
  readonly pricingReport: () => Promise<readonly PricingReportRow[]>;
}

/** The live run's source: its directory row, and `…/runs/{run_id}/report/*`. */
export function runLineSource(runId: string | null): LineSource {
  return {
    context: () => readExportContext(runId),
    replenishmentReport: () => (runId === null
      ? Promise.resolve([])
      : collectFresh(replenishmentReport, { path: { run_id: runId } }, i18next.language)),
    pricingReport: () => (runId === null ? Promise.resolve([]) : collectPages(pricingReport, { path: { run_id: runId } })),
  };
}

/**
 * The run's replenishment report, for the file's product, store and sales columns
 * (`./report-attributes.ts`). Never rejects: an unread report leaves every line's attributes absent
 * with `not_in_report`, and the file keeps every proposal.
 */
async function readReplenishmentReport(source: LineSource): Promise<ReadonlyMap<string, ReplenishmentReportRow>> {
  try {
    return indexReplenishmentReport(await source.replenishmentReport());
  } catch {
    return new Map();
  }
}

/** The run's transfers report, for the transfer file's names, attributes and sales. Never rejects. */
async function readTransfersReportIndex(runId: string | null): Promise<ReadonlyMap<string, TransferReportRow>> {
  if (runId === null) return new Map();
  try {
    return indexTransfersReport(await collectFresh(transfersReport, { path: { run_id: runId } }, i18next.language));
  } catch {
    return new Map();
  }
}

/** Resolves a key in the planner's current locale, for resolvers that run outside React. */
const translateNow: Translate = (key, values) => i18next.t(key, values ?? {});

/**
 * What a proposal file cites. `fill_rate` is not a column: the file carries its two inputs per
 * line, and the definition says how they add up, so a reader does not average the lines.
 */
const PROPOSAL_MEASURES: readonly CitedMeasure[] = [
  {
    measure: 'need_units',
    definition:
      'Units proposed to move from DC to door in the named cycle, after the shared constraint set and priced through the shared objective config.',
  },
  {
    measure: 'fill_rate',
    definition:
      'Not a column. Sum of current_units over sum of requested_units across the rows (a ratio of sums, never a mean of per-line rates); undefined, never zero, when nothing was requested.',
    registeredIn: 'metrics-v7',
  },
  {
    measure: 'proposal_drivers',
    definition:
      'The signed need decomposition the run wrote for the line (platform ADR 6038), in pairs, summing to current_units. not-reported where the run published no decomposition; not-ranged where the line published drivers but not this one.',
  },
];

/**
 * A decision's disposition, reason code and time — the columns the transfer sheet already had. In
 * the workbook (DEC-104) the disposition is the grid's words («In sospeso», not `pending`), and a
 * pending line's reason and time are blank — no decision, nothing to show — with why in «Tecnico».
 * They read «Non assortito» on every pending line until 2026-10-02. The CSV is unchanged.
 */
/**
 * The workbook's words for the codes the screens already name (DEC-104, usability audit E2): each
 * through the screen's own key function, and the code itself where that function has no words.
 * The CSV keeps every code.
 */
export const BOUND_BY_WORDS = catalogueWords((code) => (toConstraintKey(code) === UNCLASSIFIED_CONSTRAINT_KEY ? null : toConstraintKey(code)));
const STREAM_WORDS = catalogueWords((code) => (toStreamKey(code) === UNCLASSIFIED_STREAM_KEY ? null : toStreamKey(code)));
const DELIVERY_WORDS = catalogueWords((code) => (toDeliveryStateKey(code) === UNRECOGNISED_DELIVERY_KEY ? null : toDeliveryStateKey(code)));

const DISPOSITION_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'disposition', headerKey: 'inseason.transferDecision.column.disposition', kind: 'label', descriptionKey: 'inseason.columnGuide.disposition.disposition',
    // A code the grid has no words for stays a code, never «unknown».
    display: catalogueWords((code) => {
      const key = dispositionStateKey(code === 'pending' ? null : code);
      return key === dispositionStateKey('?') ? null : key;
    }),
  },
  {
    key: 'disposition_reason_code', headerKey: 'inseason.transferDecision.column.reason', kind: 'label', descriptionKey: 'inseason.columnGuide.disposition.disposition_reason_code',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.disposition.absent.noReason' } },
  },
  {
    key: 'disposition_decided_at', headerKey: 'inseason.transferDecision.column.decidedAt', kind: 'text', descriptionKey: 'inseason.columnGuide.disposition.disposition_decided_at',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.disposition.absent.noDecision' } },
  },
  {
    // Who took the first decision, by profile name (bata-platform ADR 9021, issue 218 Phase 3).
    key: 'disposition_decided_by', headerKey: 'person.decidedBy.column', kind: 'label', display: PERSON_NAME_DISPLAY,
    descriptionKey: 'person.decidedBy.help',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.disposition.absent.noDecision' } },
  },
];

/**
 * The proposal file's own columns, in its order — the run's provenance columns follow them
 * (`withRunProvenance`). Each says what it means (`descriptionKey`, `docs/adr/9045`).
 */
const PROPOSAL_COLUMNS: readonly ExportColumn[] = [
  { key: 'proposal_id', headerKey: 'inseason.column.proposalId', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.proposal_id' },
  { key: 'store_key', headerKey: 'inseason.column.store', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.store_key' },
  { key: 'article_key', headerKey: 'inseason.column.article', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.article_key' },
  ...SIZE_EXPORT_COLUMNS,
  { key: 'mode', headerKey: 'inseason.column.mode', kind: 'label', descriptionKey: 'inseason.columnGuide.proposal.mode', display: catalogueWords((code) => (toModeKey(code) === UNCLASSIFIED_MODE_KEY ? null : toModeKey(code))) },
  { key: 'current_units', headerKey: 'inseason.why.proposed', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.current_units' },
  { key: 'requested_units', headerKey: 'inseason.why.requested', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.requested_units' },
  // «Non coperto» and why (bata-ui#302 B): requested − proposed, and the engine driver that cut it.
  { key: 'uncovered_units', headerKey: 'inseason.uncovered.column', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.proposal.uncovered_units' },
  {
    key: 'uncovered_reason', headerKey: 'inseason.uncovered.reasonColumn', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.uncovered_reason',
    absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.uncovered.absent.covered' } },
  },
  { key: 'bound_by', headerKey: 'inseason.column.boundBy', kind: 'label', descriptionKey: 'inseason.columnGuide.proposal.bound_by', display: BOUND_BY_WORDS },
  { key: 'reason_code', headerKey: 'inseason.column.reasonCode', kind: 'label', descriptionKey: 'inseason.columnGuide.proposal.reason_code' },
  {
    key: 'expected_impact', headerKey: 'inseason.column.expectedImpact', kind: 'money', currency: 'COP', polarity: 'higher-is-better',
    descriptionKey: 'inseason.columnGuide.proposal.expected_impact',
  },
  { key: 'ship_week', headerKey: 'inseason.column.shipWeek', kind: 'text', descriptionKey: 'inseason.columnGuide.proposal.ship_week' },
  ...PROPOSAL_LABEL_COLUMNS,
  ...DRIVER_COLUMNS,
  STANDARD_COLUMN,
  ...DISPOSITION_COLUMNS,
];

/**
 * Replenishment › Why's column guide (`docs/adr/9045`): the proposal file's columns and the run's
 * provenance columns, exactly the CSV's — built from the definitions, nothing read.
 */
export const ALLOCATION_WHY_COLUMN_GUIDE: ColumnGuideSource = {
  id: 'inseason.allocationWhy.table',
  titleKey: 'inseason.allocationWhy.title',
  columns: [...PROPOSAL_COLUMNS, ...PROPOSAL_ATTRIBUTE_COLUMNS, ...PROVENANCE_COLUMNS],
};

/** Replenishment › Why's file: the proposal file of the planner's filtered lines (its workbook ends with the «Colonne» sheet, as every workbook does). */
export async function allocationWhyRequest(items: readonly ProposalSummary[], runId: string | null): Promise<ExportRequest> {
  return proposalRequest(items, runId);
}

/** §4.2 — the Why page's registered export: the run's proposal file. */
export async function allocationWhyExport(): Promise<ExportRequest> {
  return proposalExport();
}

/**
 * The proposal sheet over rows already read and a context already read. Pure.
 *
 * **The dropped doors' lines are not in the table** (`./dropped-door-export.ts`): they propose nothing,
 * and their `requested_units` is the need no size run could carry. The table's totals are therefore the
 * screen's served figures; the Excel workbook keeps the dropped lines on a sheet of their own, so the
 * two sheets together are the lines it was handed, and the file states how many it left out.
 */
export function proposalSheet(
  items: readonly ProposalSummary[], runId: string | null,
  context: ExportContext, translate: Translate,
  report: ReadonlyMap<string, ReplenishmentReportRow> = new Map(),
): ExportRequest {
  const { simulation } = context;
  const { served, dropped } = splitDroppedDoors(items);
  const exclusion = exclusionOf(dropped);
  const rows: ExportCell[][] = served.map((proposal) => [
    { kind: 'text', value: proposal.proposal_id },
    { kind: 'text', value: proposal.store_key },
    { kind: 'text', value: proposal.article_key },
    // The size as the platform named it, beside its key (bata-platform#470 §1, `docs/adr/9027`).
    ...sizeExportCells(sizeName(proposal)),
    { kind: 'label', value: proposal.mode, code: proposal.mode },
    { kind: 'integer', raw: proposal.units },
    { kind: 'integer', raw: proposal.requested_units },
    ...uncoveredCells(proposal, translate),
    { kind: 'label', value: proposal.bound_by, code: proposal.bound_by },
    { kind: 'label', value: proposal.reason_code, code: proposal.reason_code },
    { kind: 'money', raw: proposal.expected_impact, currency: proposal.currency_code },
    { kind: 'text', value: proposal.ship_week },
    ...proposalLabelCells(proposal, translate),
    ...driverCells(proposal, translate),
    standardCell(proposal),
    ...(simulation === undefined ? dispositionCells(proposal) : scenarioDispositionCells(proposal)),
    ...proposalAttributeCells(report.get(proposalLineKey(proposal.proposal_id, proposal.size_key))),
  ]);
  const columns: ExportColumn[] = scenarioColumns([...PROPOSAL_COLUMNS, ...PROPOSAL_ATTRIBUTE_COLUMNS], simulation);
  const filters = [...fileFilters(runId, simulation), ...droppedFilters(exclusion)];
  const request = withRunProvenance({
    // A simulation's file is its own export, named so: the lines are the scenario's, never the live run's.
    id: simulation === undefined ? 'inseason.proposals.table' : 'inseason.simulation.proposals.table',
    titleKey: simulation === undefined ? 'inseason.proposals.title' : 'inseason.simulationLines.proposals.title',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, []),
    perimeter: {
      filters,
      sort: [{ key: 'expected_impact', direction: 'desc' }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  }, context, [...PROPOSAL_MEASURES, ...droppedMeasures(exclusion, translate)], droppedAbsentNotes(exclusion));
  return withDroppedSheet(request, dropped, translate);
}

/**
 * A line's «Non coperto» pairs and the words for why (`./uncovered.ts`). A covered line's reason is
 * `not-ranged` — there is nothing uncovered to explain — never an empty string read as «no reason».
 */
function uncoveredCells(proposal: ProposalSummary, translate: Translate): readonly ExportCell[] {
  const coverage = lineCoverage(proposal);
  const words = coverageWords(coverage, translate, FILE_LOCALE);
  return [
    { kind: 'integer', raw: coverage.kind === 'covered' ? 0 : coverage.units },
    words === null ? { kind: 'missing', state: 'not-ranged' } : { kind: 'text', value: words },
  ];
}

/** The locale a file's signed pairs are written in: digits and a sign, the same in every language. */
const FILE_LOCALE = 'en-GB';

/**
 * ADR 6031: `pending` where no disposition is recorded, never a default decision. The reason
 * and the time are `not-ranged` on a pending line: there is no decision for them to belong to.
 */
function dispositionCells(row: {
  readonly disposition?: string | null;
  readonly disposition_reason_code?: string | null;
  readonly disposition_decided_at?: string | null;
  readonly disposition_actor?: string | null;
  readonly disposition_actor_name?: string | null;
  readonly disposition_actor_name_absent_reason?: string | null;
}): readonly ExportCell[] {
  const disposition = row.disposition ?? 'pending';
  const reason = row.disposition_reason_code ?? null;
  const decidedAt = row.disposition_decided_at ?? null;
  return [
    { kind: 'label', value: disposition, code: disposition },
    reason === null || reason === '' ? { kind: 'missing', state: 'not-ranged' } : { kind: 'label', value: reason, code: reason },
    decidedAt === null || decidedAt === '' ? { kind: 'missing', state: 'not-ranged' } : { kind: 'text', value: decidedAt },
    decidedByExportCell({
      disposition: row.disposition ?? null, actor: row.disposition_actor ?? null, actorName: row.disposition_actor_name ?? null,
      actorNameAbsentReason: row.disposition_actor_name_absent_reason ?? null,
    }),
  ];
}

/**
 * The perimeter a decision file states: the run it is bounded by, or — for a simulation's file — the
 * simulation, the run it branched from and what it set (`simulationFilters`).
 */
function fileFilters(runId: string | null, simulation: SimulationView | undefined): readonly ProvenanceFilter[] {
  return simulation === undefined ? runFilter(runId) : simulationFilters(simulation);
}

/**
 * A scenario line is never decided (bata-platform ADR 6181 §6): its disposition, reason, time and
 * decider are `NULL` by construction, so the four cells are `not-ranged` — «does not apply here» —
 * and `scenarioColumns` gives each column the reason a person reads: a scenario cannot be decided.
 * Never `pending`: a pending live line is one somebody can still decide, and a scenario's is not.
 *
 * Nothing is hidden either: if the platform ever reported a disposition on a scenario line — which
 * its ADR names as the alarm that something wrote behind the fence — the cells are the live ones.
 */
function scenarioDispositionCells(row: Parameters<typeof dispositionCells>[0]): readonly ExportCell[] {
  if (row.disposition !== undefined && row.disposition !== null) return dispositionCells(row);
  return DISPOSITION_COLUMNS.map((): ExportCell => ({ kind: 'missing', state: 'not-ranged' }));
}

/**
 * The columns a file is written with: the live columns, unchanged — except that, for a simulation,
 * each disposition column names WHY it is empty (`inseason.simulationExport.absent.notDecidable`)
 * where a live column says «no decision yet». Same keys, header keys, kinds and order.
 */
function scenarioColumns(columns: readonly ExportColumn[], simulation: SimulationView | undefined): ExportColumn[] {
  if (simulation === undefined) return [...columns];
  return columns.map((column): ExportColumn => (column.key.startsWith('disposition')
    ? { ...column, absent: { notRanged: 'blank', reasonKeys: { 'not-ranged': 'inseason.simulationExport.absent.notDecidable' } } }
    : column));
}

/**
 * §4.2 — the cover position.
 *
 * Each line carries the weeks of cover of its own stock position (bata-platform ADR 6073 B1,
 * `docs/adr/6021`), so the column is the line's figure, and since ADR 6082 its on-hand too; an
 * on-hand the line does not carry leaves as `missing/not-reported` rather than as a zero. A cover
 * the line does not carry leaves as missing too — `not-reported` for a position nobody reported or
 * no position at all, `not-ranged` for a size the door does not carry or a position with no demand
 * to cover — never nought: a zero cover in a spreadsheet is a stock-out, and the CSV must not merge
 * them any more than the grid may.
 *
 * A store-total line's figures are its article's at the store, every size summed by the platform
 * (ADR 6082, `docs/adr/6023`); the `position_grain` column says which a row carries, so a total
 * is never read as one size's.
 */
export async function coverExport(given?: ExportScope): Promise<ExportRequest> {
  const { runId } = scope(given);
  // The store's name beside its code, from the run's replenishment report — the source the Stock
  // page's grid names it from (usability audit B3). Never rejects: unread, the name says why.
  const [items, names] = runId === null
    ? [[], await readRunNames(null)]
    : await Promise.all([collectPages(inseasonListProposals, { query: { run_id: runId } }), readRunNames(runId, { replenishment: true })]);
  return coverSheet(items, runId, names);
}

/** A line's own cover as a file cell (ADR 6073 B1): a figure, or why there is none — never nought. */
export function lineCoverCell(line: Pick<ProposalSummary, 'weeks_of_cover' | 'position_state' | 'position_grain'>): ExportCell {
  const cover = line.weeks_of_cover ?? null;
  if (cover !== null) return { kind: 'number', raw: cover };
  // A store-total total that is not whole is unmeasured, not a size the door does not carry (ADR 6082).
  if (isStoreTotalPosition(line)) return { kind: 'missing', state: 'not-reported' };
  const state = line.position_state ?? null;
  return state === null || state === 'not_reported' ? { kind: 'missing', state: 'not-reported' } : { kind: 'missing', state: 'not-ranged' };
}

/** The grain of a line's position as the file names it (ADR 6082): this size, or the article's total at the store. */
const POSITION_GRAIN_KEY: Readonly<Record<string, string>> = {
  size: 'inseason.position.grain.size',
  store_total: STORE_TOTAL_KEY.label,
};
const POSITION_GRAIN_WORDS = catalogueWords((code) => POSITION_GRAIN_KEY[code] ?? null);

/** A line's position grain as a file cell; a platform older than the marker publishes none. */
function positionGrainCell(line: Pick<ProposalSummary, 'position_grain'>): ExportCell {
  const grain = line.position_grain ?? null;
  return grain === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'label', value: grain, code: grain };
}

/**
 * The cover grid's sheet over lines and names already read. Pure. As the proposal file's, it leaves
 * the dropped doors' lines out of the table — their `need_units` is no request the run could send —
 * and the workbook keeps them on a sheet of their own (`./dropped-door-export.ts`).
 */
export function coverSheet(
  items: readonly ProposalSummary[], runId: string | null, names: RunNames, translate: Translate = translateNow,
): ExportRequest {
  const { served, dropped } = splitDroppedDoors(items);
  const exclusion = exclusionOf(dropped);
  const filters = [...runFilter(runId), ...droppedFilters(exclusion)];
  const rows: ExportCell[][] = served.map((proposal) => [
    { kind: 'text', value: proposal.store_key },
    ...nameExportCells([names.store(proposal.store_key)]),
    ...sizeExportCells(sizeName(proposal)),
    positionGrainCell(proposal),
    proposal.on_hand_units === undefined || proposal.on_hand_units === null
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'integer', raw: proposal.on_hand_units },
    lineCoverCell(proposal),
    { kind: 'integer', raw: proposal.requested_units },
    { kind: 'label', value: proposal.bound_by, code: proposal.bound_by },
  ]);
  const request: ExportRequest = {
    id: 'inseason.cover.grid',
    titleKey: 'inseason.cover.title',
    columns: [
      { key: 'store_key', headerKey: 'inseason.column.store', descriptionKey: 'inseason.columnGuide.cover.storeKey', kind: 'text' },
      ...nameExportColumns(['store']),
      ...SIZE_EXPORT_COLUMNS,
      { key: 'position_grain', headerKey: 'inseason.column.positionGrain', descriptionKey: 'inseason.columnGuide.cover.positionGrain', kind: 'label', display: POSITION_GRAIN_WORDS },
      { key: 'on_hand_units', headerKey: 'inseason.column.onHand', descriptionKey: 'inseason.columnGuide.cover.onHand', kind: 'integer', unit: 'pairs' },
      { key: 'weeks_of_cover', headerKey: 'inseason.column.cover', descriptionKey: 'inseason.columnGuide.cover.weeksOfCover', kind: 'number', unit: 'weeks', polarity: 'target-relative' },
      { key: 'need_units', headerKey: 'inseason.column.need', descriptionKey: 'inseason.columnGuide.cover.need', kind: 'integer', unit: 'pairs' },
      { key: 'bound_by', headerKey: 'inseason.column.boundBy', descriptionKey: 'inseason.columnGuide.cover.boundBy', kind: 'label', display: BOUND_BY_WORDS },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, [
      ...droppedMeasures(exclusion, translate),
      {
        measure: 'weeks_of_cover',
        definition:
          'Weeks of cover of the stock position the run stored at the line’s article, door and size, published on the proposal line (bata-platform ADR 6073). On a store-total line (position_grain store_total) it is the article’s at the door: projected position over weekly demand, both summed over its sizes, and absent unless every ranged size carried both (ADR 6082). Undefined at zero demand — never infinite, never zero: such a line leaves as not-ranged, and a position not reported, none at all, or a store total that is not whole, as not-reported.',
      },
      {
        measure: 'on_hand_units',
        definition:
          'Pairs on hand in the stock position the run stored at the line’s grain, published on the proposal line (bata-platform ADR 6082): one size, or on a store-total line the article’s sizes at the door summed, absent unless every ranged size was measured. Never zero for an absence.',
      },
    ]),
    perimeter: {
      filters,
      // Store, then the size's place in its run — never the opaque key's spelling.
      sort: [{ key: 'store_key', direction: 'asc' }, { key: 'size_run_position', direction: 'asc' }],
      visibleColumns: [
        'store_key', ...nameExportColumns(['store']).map((column) => column.key),
        ...SIZE_EXPORT_KEYS, 'position_grain', 'on_hand_units', 'weeks_of_cover', 'need_units', 'bound_by',
      ],
      drillPath: [],
      densityMode: 'compact',
    },
  };
  return withDroppedSheet(request, dropped, translate);
}

/** §4.1 — accuracy and bias, with the evidence flag and every null preserved. */
export async function accuracyExport(): Promise<ExportRequest> {
  const items = await collectPages(listAccuracy, { query: {} });
  const views = toAccuracyViews(items);
  const measure = (value: number | null): ExportCell =>
    value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'percent', raw: value };
  const rows: ExportCell[][] = views.map((row) => [
    { kind: 'text', value: row.dimensionLabel },
    { kind: 'integer', raw: row.observations },
    measure(row.wape),
    measure(row.bias),
    row.trackingSignal === null
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'number', raw: row.trackingSignal },
    { kind: 'label', value: row.hasEvidence ? 'measured' : 'no-actuals', code: row.hasEvidence ? 'measured' : 'no_actuals' },
  ]);
  return {
    id: 'inseason.forecast.chart',
    titleKey: 'inseason.forecast.title',
    columns: [
      { key: 'dimension', headerKey: 'inseason.column.accuracyDimension', kind: 'text', descriptionKey: 'inseason.help.forecastScreen.colGroup' },
      { key: 'observations', headerKey: 'inseason.column.observations', kind: 'integer', descriptionKey: 'inseason.columnGuide.accuracy.observations' },
      { key: 'wape', headerKey: 'inseason.column.wape', kind: 'percent', polarity: 'lower-is-better', descriptionKey: 'inseason.columnGuide.accuracy.wape' },
      { key: 'bias', headerKey: 'inseason.column.bias', kind: 'percent', polarity: 'target-relative', descriptionKey: 'inseason.columnGuide.accuracy.bias' },
      { key: 'tracking_signal', headerKey: 'inseason.column.trackingSignal', kind: 'number', descriptionKey: 'inseason.columnGuide.accuracy.trackingSignal' },
      { key: 'evidence', headerKey: 'inseason.column.evidence', kind: 'label', descriptionKey: 'inseason.columnGuide.accuracy.evidence' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([], [
      {
        measure: 'forecast_bias',
        definition:
          'Signed bias from the accuracy warehouse, computed on unconstrained demand. A group with no actuals in the window is reported as having no evidence rather than as having no error, and leaves as not-reported rather than as zero.',
      },
    ]),
    perimeter: {
      filters: [],
      sort: [{ key: 'dimension', direction: 'asc' }],
      visibleColumns: ['dimension', 'observations', 'wape', 'bias', 'tracking_signal', 'evidence'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** §4.3 — the in-season open position, per class, cluster and period. */
export async function bridgeExport(): Promise<ExportRequest> {
  const items = await collectPages(listOpenPositions, { query: {} });
  const rows: ExportCell[][] = items.map((row) => [
    { kind: 'text', value: row.class_key },
    { kind: 'text', value: row.cluster_key },
    { kind: 'text', value: row.period_key },
    { kind: 'number', raw: row.open_value },
    { kind: 'number', raw: row.on_order_committed },
    { kind: 'number', raw: row.planned_receipts },
    { kind: 'number', raw: row.received },
    { kind: 'number', raw: row.reforecast_delta },
    { kind: 'number', raw: row.markdown_reserve_delta },
    { kind: 'label', value: row.is_overspent ? 'overspent' : 'within', code: row.is_overspent ? 'overspent' : 'within_envelope' },
  ]);
  return {
    id: 'inseason.openToBuy.chart',
    titleKey: 'inseason.openToBuy.title',
    columns: [
      { key: 'class_key', headerKey: 'inseason.column.classKey', descriptionKey: 'inseason.help.openToBuyScreen.colClass', kind: 'text' },
      { key: 'cluster_key', headerKey: 'inseason.column.cluster', descriptionKey: 'inseason.help.openToBuyScreen.colCluster', kind: 'text' },
      { key: 'period_key', headerKey: 'inseason.column.period', descriptionKey: 'inseason.help.openToBuyScreen.colPeriod', kind: 'text' },
      { key: 'open_value', headerKey: 'inseason.column.openValue', descriptionKey: 'inseason.columnGuide.openToBuy.openValue', kind: 'number' },
      { key: 'on_order_committed', headerKey: 'inseason.column.onOrder', descriptionKey: 'inseason.help.openToBuyScreen.colOnOrder', kind: 'number' },
      { key: 'planned_receipts', headerKey: 'inseason.column.plannedReceipts', descriptionKey: 'inseason.help.openToBuyScreen.colPlannedReceipts', kind: 'number' },
      { key: 'received', headerKey: 'inseason.column.received', descriptionKey: 'inseason.help.openToBuyScreen.colReceived', kind: 'number' },
      { key: 'reforecast_delta', headerKey: 'inseason.column.reforecastDelta', descriptionKey: 'inseason.help.openToBuyScreen.colReforecastDelta', kind: 'number' },
      { key: 'markdown_reserve_delta', headerKey: 'inseason.column.markdownReserveDelta', descriptionKey: 'inseason.help.openToBuyScreen.colMarkdownReserveDelta', kind: 'number' },
      { key: 'is_overspent', headerKey: 'inseason.column.overspent', descriptionKey: 'inseason.columnGuide.openToBuy.overspent', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([], [
      {
        measure: 'open_to_buy_value',
        definition:
          'The open position recomputed on chapter 02’s OTB object — one object in two lifecycle states, never a separate in-season model. `is_overspent` is the service’s own flag and is not re-derived downstream.',
      },
      {
        measure: 'net_sales_value',
        definition:
          'The plan-to-landing bridge is NOT part of this export: no read publishes the season-end landing decomposition for a plan version, and the screen does not draw one.',
      },
    ]),
    perimeter: {
      filters: [],
      sort: [{ key: 'period_key', direction: 'asc' }],
      visibleColumns: ['class_key', 'cluster_key', 'period_key', 'open_value', 'on_order_committed', 'planned_receipts', 'received', 'reforecast_delta', 'markdown_reserve_delta', 'is_overspent'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * §4.4 — the markdown pages' exports, one per page (issue 226, giro 3 «Dopo» point 1).
 *
 * **One resolver used to answer four panels.** The Output page held the run's price steps, a
 * price path, a carrying-cost frontier and a batch of options, and `markdownExport` read a
 * `?markdownView=` key to tell which one the planner was looking at (ADR 0058). Each of those is a
 * page now, so each page registers its own export and the page IS the view:
 *
 *   - Result (`inseason.markdown.result`) — the solved path of the option on screen, or, for a
 *     several-option question with none opened, one row per option (the «Options solved» list);
 *   - the run's Calendar (`inseason.markdown.calendar`) — the run's price steps;
 *   - the Frontier (`inseason.markdown.frontier`) — every solved point, none recommended.
 *
 * Every resolver reads the SAME operation its page reads, through the same transport. `12` §2.4
 * control 2 forbids a hand-rolled request and there is none here.
 */
export async function markdownResultExport(given?: ExportScope): Promise<ExportRequest> {
  const current = scope(given);
  const open = openedOption(current, given);
  if (current.markdownPath.optionKeys.length > 1 && open === null) return batchExport(current);
  return pathExport(current, open ?? current.markdownPath.optionKeys[0] ?? '');
}

/** The run's price steps — what the run's Calendar shows. */
export async function markdownCalendarExport(given?: ExportScope): Promise<ExportRequest> {
  return priceActionExport(scope(given));
}

/**
 * The margin-versus-cash frontier of the option on screen. A several-option question with none
 * opened poses no frontier — the page says so — and the file is empty rather than about an option
 * the planner did not choose.
 */
export async function markdownFrontierExport(given?: ExportScope): Promise<ExportRequest> {
  const current = scope(given);
  const open = openedOption(current, given);
  const keys = current.markdownPath.optionKeys;
  return frontierExport(current, keys.length > 1 ? open : keys[0] ?? null);
}

/** The several-option question's opened option, from the same search string the page reads. */
function openedOption(current: InSeasonScope, given?: ExportScope): string | null {
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  return readOpenOption(search, current.markdownPath.optionKeys);
}

/**
 * The query every markdown read shares, or `null` where the URL does not state one.
 *
 * Built here rather than imported from the screen because the screen builds it inside a
 * component; duplicating eleven field names is the visible cost of keeping the resolver
 * free of React, and the alternative — a hook — cannot be called from an export.
 */
function markdownQuery(current: InSeasonScope, optionKey: string): {
  readonly query: Record<string, string | number>;
} | null {
  const values = current.markdownPath.values;
  if (values === null) return null;
  return {
    query: {
      country_key: values.countryKey,
      brand_key: values.brandKey,
      option_key: optionKey,
      cluster_key: values.clusterKey,
      channel_key: values.channelKey,
      model_version_id: values.modelVersionId,
      as_of: current.asOf ?? '',
      weeks_remaining: Number(values.weeksRemaining),
      minor_unit: Number(values.minorUnit),
      ...(current.markdownPath.openingUnits === null
        ? {}
        : { opening_units: Number(current.markdownPath.openingUnits) }),
      ...objectiveQuery(current.markdownPath.objective),
    },
  };
}

/** The perimeter of a path or frontier export: the option, not a run. */
function optionFilter(current: InSeasonScope): readonly ProvenanceFilter[] {
  const values = current.markdownPath.values;
  const keys = current.markdownPath.optionKeys;
  if (values === null) {
    return [{ field: 'option_key', operator: 'unset', value: '' }];
  }
  return [
    { field: 'option_key', operator: keys.length > 1 ? 'in' : 'equals', value: keys.join(',') },
    { field: 'country_key', operator: 'equals', value: values.countryKey },
    { field: 'brand_key', operator: 'equals', value: values.brandKey },
    { field: 'cluster_key', operator: 'equals', value: values.clusterKey },
    { field: 'channel_key', operator: 'equals', value: values.channelKey },
    { field: 'model_version_id', operator: 'equals', value: values.modelVersionId },
    { field: 'as_of', operator: 'equals', value: current.asOf ?? '' },
    { field: 'weeks_remaining', operator: 'equals', value: values.weeksRemaining },
    {
      field: 'objective_kind',
      operator: current.markdownPath.objective === null ? 'unset' : 'equals',
      value: current.markdownPath.objective?.kind ?? '',
    },
  ];
}

/**
 * The measure definitions the markdown views share.
 *
 * `markdown_trade_off_frontier` is no longer among them as a refusal, because the
 * frontier is now measured and exported. The base-and-window statement stays: reqs/04 §2
 * records the same dataset producing three different markdown rates depending on
 * clipping, window and denominator, and a spreadsheet that left the base implicit would
 * reintroduce exactly that.
 */
const MARKDOWN_MEASURES = [
  {
    measure: 'realised_price',
    definition:
      'Net realised value per unit sold. Markdown rate is TTM, unclamped, ratio-of-sums, measured against gross at list. Both the units-weighted and the value-weighted rate are published and named; neither is "the" markdown rate.',
  },
  {
    measure: 'terminal_stock_units',
    definition:
      'Units expected to survive to the exit window. Gate D4 measures realised-price improvement at CONSTANT terminal stock, so a comparison without both terminal figures is not the gate.',
  },
  {
    measure: 'opening_units',
    definition:
      'Sellable on-hand units at STORE locations only, from the stock batch in force. The estate carries no distribution-centre and no in-transit position at all, so this is the selling position and not the total position — which is the perimeter a markdown opens from by decision of 2026-09-13.',
  },
];

/**
 * What a price-step export's path columns mean. Each is the PATH's figure — one article x
 * cluster in the run — written by `price_cycle` on EVERY step row of that path, so a file of
 * steps repeats it; the definition says so, because a spreadsheet summed down the column would
 * count a four-step path four times.
 */
const PRICE_STEP_PATH_MEASURES = [
  {
    measure: 'inseason.expected_margin_recovery',
    definition:
      'The expected margin recovery of the PATH the step belongs to (the article x cluster in the run): the total contribution the optimiser projects for the whole path, repeated on every step row of that path. Sum it once per path, never over rows. Expected, not realised.',
  },
  {
    measure: 'inseason.terminal_units_projected',
    definition:
      'The projected terminal units of the PATH the step belongs to, against its terminal target, repeated on every step row of that path. Count each path once, never sum over rows.',
  },
];

/** The solved price path, week by week — what Result shows for one option. */
async function pathExport(current: InSeasonScope, optionKey: string): Promise<ExportRequest> {
  const params = markdownQuery(current, optionKey);
  const solved = params === null
    ? null
    : await callOperation(markdownPath, params as never, {});
  const rows: ExportCell[][] = solved === null ? [] : solved.steps.map((step) => [
    { kind: 'integer', raw: step.week },
    { kind: 'money', raw: step.price, currency: solved.currency_code },
    { kind: 'integer', raw: step.opening_units },
    { kind: 'number', raw: step.expected_units },
    { kind: 'number', raw: step.closing_units },
    { kind: 'money', raw: step.margin, currency: solved.currency_code },
    { kind: 'money', raw: step.carrying_cost, currency: solved.currency_code },
    // Repeated on every row rather than left to a header the CSV has no room for: a
    // spreadsheet outlives the screen, and a path whose feasibility verdict and
    // objective are not on the row can be filtered, sorted and pasted into a deck
    // without either. The verdict is a label rather than a boolean for the same reason.
    { kind: 'label', value: solved.is_feasible ? 'feasible' : 'infeasible', code: solved.is_feasible ? 'feasible' : 'infeasible' },
    { kind: 'label', value: solved.objective_kind, code: solved.objective_kind },
    { kind: 'text', value: solved.ladder_id },
  ]);
  return {
    id: 'inseason.markdown.result',
    titleKey: 'inseason.markdown.pathTitle',
    columns: [
      { key: 'week', headerKey: 'inseason.column.week', descriptionKey: 'inseason.columnGuide.markdownPath.week', kind: 'integer' },
      { key: 'price', headerKey: 'inseason.column.price', descriptionKey: 'inseason.columnGuide.markdownPath.price', kind: 'money', currency: solved?.currency_code ?? 'COP', polarity: 'higher-is-better' },
      { key: 'opening_units', headerKey: 'inseason.column.openingUnits', descriptionKey: 'inseason.columnGuide.markdownPath.openingUnits', kind: 'integer', unit: 'pairs' },
      { key: 'expected_units', headerKey: 'inseason.column.expectedUnits', descriptionKey: 'inseason.columnGuide.markdownPath.expectedUnits', kind: 'number', unit: 'pairs' },
      { key: 'closing_units', headerKey: 'inseason.column.closingUnits', descriptionKey: 'inseason.columnGuide.markdownPath.closingUnits', kind: 'number', unit: 'pairs' },
      { key: 'margin', headerKey: 'inseason.column.margin', descriptionKey: 'inseason.columnGuide.markdownPath.margin', kind: 'money', currency: solved?.currency_code ?? 'COP', polarity: 'higher-is-better' },
      { key: 'carrying_cost', headerKey: 'inseason.column.carryingCost', descriptionKey: 'inseason.columnGuide.markdownPath.carryingCost', kind: 'money', currency: solved?.currency_code ?? 'COP', polarity: 'lower-is-better' },
      { key: 'is_feasible', headerKey: 'inseason.column.feasible', descriptionKey: 'inseason.columnGuide.markdownPath.feasible', kind: 'label' },
      { key: 'objective_kind', headerKey: 'inseason.column.objectiveKind', descriptionKey: 'inseason.columnGuide.markdownPath.objectiveKind', kind: 'label' },
      { key: 'ladder_id', headerKey: 'inseason.column.ladderId', descriptionKey: 'inseason.columnGuide.markdownPath.ladderId', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(optionFilter(current), MARKDOWN_MEASURES),
    perimeter: {
      filters: optionFilter(current),
      sort: [{ key: 'week', direction: 'asc' }],
      visibleColumns: ['week', 'price', 'opening_units', 'expected_units', 'closing_units', 'margin', 'carrying_cost', 'is_feasible', 'objective_kind', 'ladder_id'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The margin-versus-cash frontier — what the trade-off panel shows.
 *
 * Every point, feasible and infeasible, and no recommended one. §4.4 requires competing
 * scenarios with a financial bridge before commit, and a spreadsheet with a highlighted
 * row would be the hidden weighting arriving in the artefact that outlives the screen.
 */
async function frontierExport(current: InSeasonScope, optionKey: string | null): Promise<ExportRequest> {
  const params = optionKey === null ? null : markdownQuery(current, optionKey);
  const solved = params === null
    ? null
    : await callOperation(markdownFrontier, params as never, {});
  const rows: ExportCell[][] = solved === null ? [] : solved.points.map((point) => [
    { kind: 'number', raw: point.carrying_cost_rate },
    { kind: 'money', raw: point.carrying_cost_per_unit_week, currency: solved.currency_code },
    { kind: 'label', value: point.is_feasible ? 'feasible' : 'infeasible', code: point.is_feasible ? 'feasible' : 'infeasible' },
    { kind: 'integer', raw: point.markdown_actions },
    { kind: 'money', raw: point.realised_price, currency: solved.currency_code },
    { kind: 'number', raw: point.terminal_units },
    { kind: 'money', raw: point.total_contribution, currency: solved.currency_code },
  ]);
  return {
    id: 'inseason.markdown.frontier',
    titleKey: 'inseason.markdown.tradeOffTitle',
    columns: [
      { key: 'carrying_cost_rate', headerKey: 'inseason.column.carryingCostRate', descriptionKey: 'inseason.columnGuide.markdownPath.carryingCostRate', kind: 'number' },
      { key: 'carrying_cost_per_unit_week', headerKey: 'inseason.column.carryingCostPerUnitWeek', descriptionKey: 'inseason.columnGuide.markdownPath.carryingCostPerUnitWeek', kind: 'money', currency: solved?.currency_code ?? 'COP', polarity: 'lower-is-better' },
      { key: 'is_feasible', headerKey: 'inseason.column.feasible', descriptionKey: 'inseason.columnGuide.markdownPath.feasible', kind: 'label' },
      { key: 'markdown_actions', headerKey: 'inseason.column.markdownActions', descriptionKey: 'inseason.columnGuide.markdownPath.markdownActions', kind: 'integer' },
      { key: 'realised_price', headerKey: 'inseason.column.realisedPrice', descriptionKey: 'inseason.columnGuide.markdownPath.realisedPrice', kind: 'money', currency: solved?.currency_code ?? 'COP', polarity: 'higher-is-better' },
      { key: 'terminal_units', headerKey: 'inseason.column.terminalUnits', descriptionKey: 'inseason.columnGuide.markdownPath.terminalUnits', kind: 'number', unit: 'pairs', polarity: 'target-relative' },
      { key: 'total_contribution', headerKey: 'inseason.column.contribution', descriptionKey: 'inseason.columnGuide.markdownPath.totalContribution', kind: 'money', currency: solved?.currency_code ?? 'COP', polarity: 'higher-is-better' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(optionFilter(current), [
      ...MARKDOWN_MEASURES,
      {
        measure: 'markdown_trade_off_frontier',
        definition:
          'The same option solved at each carrying-cost rate the markdown registry holds. NO RECOMMENDED POINT is published, here or in the service: §4.4 requires margin against cash to be evaluated as competing scenarios, and choosing between them is a finance decision with an owner.',
      },
    ]),
    perimeter: {
      filters: optionFilter(current),
      sort: [{ key: 'carrying_cost_rate', direction: 'asc' }],
      visibleColumns: ['carrying_cost_rate', 'carrying_cost_per_unit_week', 'is_feasible', 'markdown_actions', 'realised_price', 'terminal_units', 'total_contribution'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * One row per option asked about — what the batch grid shows, refusals included.
 *
 * A refused option keeps its row and carries the platform's own code, for the reason the
 * panel gives at length: an export that listed only the solved rows would hide the
 * majority of what the planner asked about, and would hide it in a way that looks like an
 * answer. The numeric cells of a refused row are `missing` with the state on them, never
 * zero — a zero contribution would read as an option worth nothing rather than as one
 * nobody could work out.
 */
async function batchExport(current: InSeasonScope): Promise<ExportRequest> {
  const keys = current.markdownPath.optionKeys.slice(0, BATCH_LIMIT);
  const settled = await Promise.all(keys.map(async (optionKey) => {
    const params = markdownQuery(current, optionKey);
    if (params === null) return { optionKey, path: null, code: null };
    try {
      return {
        optionKey,
        path: await callOperation(markdownPath, params as never, {}) as AssembledMarkdownPath,
        code: null,
      };
    } catch (error) {
      return { optionKey, path: null, code: refusalCode(error) };
    }
  }));
  const rows: ExportCell[][] = settled.map((row) => {
    // The refusal code rides in its own column; `MissingState` is reqs/00 D8's closed
    // three-member vocabulary and a platform refusal code is not a member of it. An
    // unsolved cell is therefore `not-reported` — nothing was measured here — and the
    // machine code that says WHY is beside it rather than smuggled into the state.
    const state = 'not-reported' as const;
    // A solved row has no refusal: the cell is empty, not `not_solved`. A row with no path and no
    // platform code was never asked (its question could not be built), and says so.
    const code = row.path === null ? (row.code ?? 'not_solved') : null;
    return [
      { kind: 'text', value: row.optionKey },
      { kind: 'label', value: row.path === null ? 'refused' : 'solved', code: row.path === null ? 'refused' : 'solved' },
      code === null ? { kind: 'text', value: '' } : { kind: 'label', value: code, code },
      row.path === null ? { kind: 'missing', state } : { kind: 'integer', raw: row.path.opening_units },
      row.path === null ? { kind: 'missing', state } : { kind: 'integer', raw: row.path.markdown_actions },
      row.path === null ? { kind: 'missing', state } : { kind: 'money', raw: row.path.realised_price, currency: row.path.currency_code },
      row.path === null ? { kind: 'missing', state } : { kind: 'money', raw: row.path.total_contribution, currency: row.path.currency_code },
      row.path === null ? { kind: 'missing', state } : { kind: 'number', raw: row.path.terminal_units },
    ];
  });
  return {
    id: 'inseason.markdown.result',
    titleKey: 'inseason.markdown.batchTitle',
    columns: [
      { key: 'option_key', headerKey: 'inseason.column.option', kind: 'text', descriptionKey: 'inseason.columnGuide.markdownPath.option' },
      { key: 'outcome', headerKey: 'inseason.column.outcome', kind: 'label', descriptionKey: 'inseason.columnGuide.markdownPath.outcome' },
      { key: 'refusal_code', headerKey: 'inseason.column.refusalCode', kind: 'label', descriptionKey: 'inseason.columnGuide.markdownBatch.refusal_code' },
      { key: 'opening_units', headerKey: 'inseason.column.openingUnits', kind: 'integer', unit: 'pairs', descriptionKey: 'inseason.columnGuide.markdownPath.batchOpeningUnits' },
      { key: 'markdown_actions', headerKey: 'inseason.column.markdownActions', kind: 'integer', descriptionKey: 'inseason.columnGuide.markdownPath.markdownActions' },
      { key: 'realised_price', headerKey: 'inseason.column.realisedPrice', kind: 'money', currency: 'COP', polarity: 'higher-is-better', descriptionKey: 'inseason.columnGuide.markdownPath.realisedPrice' },
      { key: 'total_contribution', headerKey: 'inseason.column.contribution', kind: 'money', currency: 'COP', polarity: 'higher-is-better', descriptionKey: 'inseason.columnGuide.markdownPath.totalContribution' },
      { key: 'terminal_units', headerKey: 'inseason.column.terminalUnits', kind: 'number', unit: 'pairs', polarity: 'target-relative', descriptionKey: 'inseason.columnGuide.markdownPath.terminalUnits' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(optionFilter(current), [
      ...MARKDOWN_MEASURES,
      {
        measure: 'markdown_batch_outcome',
        definition:
          'Every option the scope named gets a row, solved independently. Figures from different options are NOT addends: they are solved at different positions and horizons, and no total is published for that reason.',
      },
    ]),
    perimeter: {
      filters: optionFilter(current),
      sort: [{ key: 'option_key', direction: 'asc' }],
      visibleColumns: ['option_key', 'outcome', 'refusal_code', 'opening_units', 'markdown_actions', 'realised_price', 'total_contribution', 'terminal_units'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The run-scoped price-action queue — what the actions panel shows. */
async function priceActionExport(current: InSeasonScope): Promise<ExportRequest> {
  const runId = current.runId;
  const items = runId === null
    ? []
    : await collectPages(listPriceActions, { query: { run_id: runId } });
  return priceActionRequest(items, runId);
}

/**
 * The price-action sheet over actions already read, in the order given — the CSV under the
 * recommendation table and under a week's expanded steps (`docs/adr/9008`), each handing in
 * the actions it draws, in the order it draws them.
 */
export async function priceActionRequest(
  items: readonly PriceActionSummary[], runId: string | null, source: LineSource = runLineSource(runId),
): Promise<ExportRequest> {
  const [context, report] = await Promise.all([source.context(), readPricingReport(source)]);
  return priceActionSheet(items, runId, context, translateNow, report);
}

/**
 * The run's pricing report (`GET /inseason/runs/{run_id}/report/pricing`, bata-platform ADR 9018
 * as amended), for the price-step file's labels, stock, recorded reason, constraints, guardrails,
 * unit cost and decided price (`./report-attributes.ts`). Never rejects: an unread report — a
 * platform before it, or `planning.inseason.report_unavailable` — leaves every step's attributes
 * absent with `not_in_report`, and the file keeps every step.
 */
async function readPricingReport(source: LineSource): Promise<ReadonlyMap<string, PricingReportRow>> {
  try {
    return indexPricingReport(await source.pricingReport());
  } catch {
    return new Map();
  }
}

/**
 * What a price-step file cites. `expected_margin_recovery` is registered (metrics-v7) as the
 * run's sum of the steps' figure; the column is that figure per step, repeated per path.
 */
const PRICE_STEP_MEASURES: readonly CitedMeasure[] = [
  ...MARKDOWN_MEASURES.map((entry) => (entry.measure === 'terminal_stock_units'
    ? { ...entry, registeredIn: 'metrics-v1' }
    : entry)),
  ...PRICE_STEP_PATH_MEASURES.map((entry) => (entry.measure === 'inseason.expected_margin_recovery'
    ? { ...entry, measure: 'expected_margin_recovery', registeredIn: 'metrics-v7' }
    : entry)),
];

/**
 * What a step was priced on (bata-reqs issue 35, ADR 6024): the recorded code, in words in the
 * workbook; a step written before the run recorded it is `not-reported`, never a guessed basis.
 */
const BASIS_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'elasticity_basis', headerKey: 'inseason.priceBasis.elasticityTitle', descriptionKey: 'inseason.columnGuide.priceStep.elasticityBasis', kind: 'label',
    display: catalogueWords((code) => { const basis = elasticityBasis(code); return basis === null || !basis.known ? null : basis.labelKey; }),
    absent: { reasonKeys: { 'not-reported': 'inseason.priceBasis.notRecorded' } },
  },
  {
    key: 'original_price_basis', headerKey: 'inseason.priceBasis.originalPriceTitle', descriptionKey: 'inseason.columnGuide.priceStep.originalPriceBasis', kind: 'label',
    display: catalogueWords((code) => { const basis = originalPriceBasis(code); return basis === null || !basis.known ? null : basis.labelKey; }),
    absent: { reasonKeys: { 'not-reported': 'inseason.priceBasis.notRecorded' } },
  },
  {
    // Platform ADR 6085: the pool a pooled elasticity came from, verbatim. Absent on every step
    // not priced on a pool, and on a step written before the run recorded it.
    key: 'elasticity_pool_key', headerKey: 'inseason.priceBasis.poolTitle', descriptionKey: 'inseason.columnGuide.priceStep.elasticityPool', kind: 'text',
    absent: { reasonKeys: { 'not-reported': 'inseason.priceBasis.poolNone' } },
  },
  {
    // Platform ADR 6085 amendment (docs/adr/6026): the pool an own elasticity was shrunk toward,
    // verbatim, and the article's own weight. The basis stays `fitted`; these two say how much of
    // it is the article's. Absent on a step not shrunk, and on one written before runs recorded it.
    key: 'elasticity_shrunk_toward', headerKey: 'inseason.priceBasis.shrunkTowardTitle', descriptionKey: 'inseason.columnGuide.priceStep.shrunkToward', kind: 'text',
    absent: { reasonKeys: { 'not-reported': 'inseason.priceBasis.shrunkTowardNone' } },
  },
  {
    key: 'elasticity_own_weight', headerKey: 'inseason.priceBasis.ownWeightTitle', descriptionKey: 'inseason.columnGuide.priceStep.ownWeight', kind: 'percent', decimals: 0,
    absent: { reasonKeys: { 'not-reported': 'inseason.priceBasis.ownWeightNone' } },
  },
];

function basisCells(step: StepBases): readonly ExportCell[] {
  const pool = step.elasticity_pool_key;
  const shrinkage = shrinkageOf(step);
  return [
    ...[step.elasticity_basis, step.original_price_basis].map((code): ExportCell => (
      code === null || code === undefined || code === ''
        ? { kind: 'missing', state: 'not-reported' }
        : { kind: 'label', value: code, code }
    )),
    pool === null || pool === undefined || pool === '' ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value: pool },
    shrinkage === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value: shrinkage.toward },
    shrinkage === null || shrinkage.ownWeight === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'percent', raw: shrinkage.ownWeight },
  ];
}

/**
 * How a step's price was rounded on the shelf (bata-reqs issue 38 R4, platform ADR 6190,
 * `docs/adr/9067`): the mode in words, the target and the realised discount, the shelf price. A step
 * priced with no rounding mode, or written before the run recorded it, is `not-reported`.
 */
const ROUNDING_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'rounding_mode', headerKey: 'inseason.priceRounding.mode', kind: 'label', descriptionKey: 'inseason.columnGuide.markdownRounding.mode',
    display: catalogueWords((code) => roundingModeLabelKey(code)),
    absent: { reasonKeys: { 'not-reported': 'inseason.priceRounding.notRecorded' } },
  },
  {
    key: 'target_depth', headerKey: 'inseason.priceRounding.target', kind: 'percent', decimals: 1, descriptionKey: 'inseason.columnGuide.executionPack.targetDepth',
    absent: { reasonKeys: { 'not-reported': 'inseason.priceRounding.notRecorded' } },
  },
  {
    key: 'realised_depth', headerKey: 'inseason.priceRounding.realised', kind: 'percent', decimals: 1, descriptionKey: 'inseason.columnGuide.executionPack.realisedDepth',
    absent: { reasonKeys: { 'not-reported': 'inseason.priceRounding.notRecorded' } },
  },
  {
    key: 'shelf_price', headerKey: 'inseason.priceRounding.shelfPrice', kind: 'money', currency: 'COP', descriptionKey: 'inseason.columnGuide.executionPack.shelfPrice',
    absent: { reasonKeys: { 'not-reported': 'inseason.priceRounding.notRecorded' } },
  },
];

function roundingCells(step: PriceActionSummary | undefined): readonly ExportCell[] {
  const rounding = step?.rounding ?? null;
  const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
  if (rounding === null || step === undefined) return [missing, missing, missing, missing];
  const share = (value: number | null | undefined): ExportCell => (
    typeof value === 'number' && Number.isFinite(value) ? { kind: 'percent', raw: value } : missing
  );
  return [
    { kind: 'label', value: rounding.mode, code: rounding.mode },
    share(rounding.target_depth),
    share(rounding.realised_depth),
    typeof rounding.shelf_price === 'number' ? { kind: 'money', raw: rounding.shelf_price, currency: step.currency_code } : missing,
  ];
}

/** The price-step sheet over steps and a context already read. Pure. */
export function priceActionSheet(
  items: readonly PriceActionSummary[], runId: string | null,
  context: ExportContext, translate: Translate,
  report: ReadonlyMap<string, PricingReportRow> = new Map(),
): ExportRequest {
  const { simulation } = context;
  const byId = new Map(items.map((item) => [item.price_action_id, item]));
  const rows: ExportCell[][] = toPriceActionRows(items).map((row) => [
    { kind: 'text', value: row.priceActionId },
    { kind: 'text', value: row.articleKey },
    { kind: 'text', value: row.clusterKey },
    { kind: 'text', value: row.effectiveWeek },
    { kind: 'money', raw: row.fromPrice, currency: row.currencyCode },
    { kind: 'money', raw: row.toPrice, currency: row.currencyCode },
    { kind: 'percent', raw: row.depthFromFullPrice },
    // The recovery the recommendation table shows beside the depth (`docs/adr/9008`).
    { kind: 'money', raw: row.expectedMarginRecovery, currency: row.currencyCode },
    { kind: 'label', value: row.priceStream, code: row.priceStream },
    { kind: 'integer', raw: row.terminalUnitsProjected },
    { kind: 'integer', raw: row.terminalTargetUnits },
    { kind: 'text', value: translate(toStreamKey(row.priceStream)) },
    ...basisCells(byId.get(row.priceActionId) ?? {}),
    ...roundingCells(byId.get(row.priceActionId)),
    ...(simulation === undefined ? dispositionCells(byId.get(row.priceActionId) ?? {}) : scenarioDispositionCells(byId.get(row.priceActionId) ?? {})),
    ...pricingAttributeCells(report.get(row.priceActionId)),
  ]);
  const columns: ExportColumn[] = scenarioColumns([
    { key: 'price_action_id', headerKey: 'inseason.column.priceActionId', descriptionKey: 'inseason.columnGuide.priceStep.priceActionId', kind: 'text' },
    { key: 'article_key', headerKey: 'inseason.column.article', descriptionKey: 'inseason.columnGuide.priceStep.articleKey', kind: 'text' },
    { key: 'cluster_key', headerKey: 'inseason.column.cluster', descriptionKey: 'inseason.columnGuide.priceStep.clusterKey', kind: 'text' },
    { key: 'effective_week', headerKey: 'inseason.column.effectiveWeek', descriptionKey: 'inseason.columnGuide.priceStep.effectiveWeek', kind: 'text' },
    { key: 'from_price', headerKey: 'inseason.column.fromPrice', descriptionKey: 'inseason.columnGuide.priceStep.fromPrice', kind: 'money', currency: 'COP' },
    { key: 'to_price', headerKey: 'inseason.column.toPrice', descriptionKey: 'inseason.columnGuide.priceStep.toPrice', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
    { key: 'depth_from_full_price', headerKey: 'inseason.column.depth', descriptionKey: 'inseason.columnGuide.priceStep.depth', kind: 'percent' },
    { key: 'expected_margin_recovery', headerKey: 'inseason.priceDecision.detail.recovery', descriptionKey: 'inseason.columnGuide.priceStep.recovery', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
    { key: 'price_stream', headerKey: 'inseason.column.priceStream', descriptionKey: 'inseason.columnGuide.priceStep.priceStream', kind: 'label', display: STREAM_WORDS },
    { key: 'terminal_units_projected', headerKey: 'inseason.column.terminalUnits', descriptionKey: 'inseason.columnGuide.priceStep.terminalUnits', kind: 'integer', unit: 'pairs' },
    { key: 'terminal_target_units', headerKey: 'inseason.column.terminalTarget', descriptionKey: 'inseason.columnGuide.priceStep.terminalTarget', kind: 'integer', unit: 'pairs' },
    { key: 'price_stream_label', headerKey: 'inseason.exportDrivers.column.priceStreamLabel', descriptionKey: 'inseason.columnGuide.priceStep.streamLabel', kind: 'text' },
    ...BASIS_COLUMNS,
    ...ROUNDING_COLUMNS,
    ...DISPOSITION_COLUMNS,
    ...PRICING_ATTRIBUTE_COLUMNS,
  ], simulation);
  const filters = fileFilters(runId, simulation);
  return withRunProvenance({
    id: simulation === undefined ? 'inseason.markdown.calendar' : 'inseason.simulation.priceSteps.table',
    titleKey: simulation === undefined ? 'inseason.markdown.title' : 'inseason.simulationLines.priceSteps.title',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, []),
    perimeter: {
      filters,
      sort: [{ key: 'effective_week', direction: 'asc' }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  }, context, PRICE_STEP_MEASURES);
}

/** §4.5 — transfer proposals, with the economics that justified each. */
export async function transferExport(): Promise<ExportRequest> {
  const { runId } = scope();
  const items = runId === null
    ? []
    : await collectPages(listTransfers, { query: { run_id: runId } });
  // Grouped the way the screen groups them, and ordered by the same rule the panel uses:
  // the named purposes in vocabulary order, then the codes this build cannot name. The
  // export is the view or it is a second, disagreeing read of the same run (G2.4).
  const ordered = toTransferActionGroups(toTransferRows(items)).flatMap((group) => group.rows);
  const [context, report, reasons, parameters] = await Promise.all([
    readExportContext(runId), readTransfersReportIndex(runId), readTransferReasons(), readInSeasonRunParameters(runId),
  ]);
  // bata-reqs ADR 9001 decision 5: the workbook carries the run's own parameters in force.
  return withParametersInForce(transferSheet(
    ordered, runId, [{ key: 'purpose', direction: 'asc' }, { key: 'net_benefit', direction: 'desc' }],
    context, translateNow, report, reasons,
  ), parameters);
}

/**
 * The transfer decision reasons' words, for the workbook (usability audit E1). Never rejects: an
 * unread vocabulary leaves each reason as its code, as the «every why field» file does.
 */
async function readTransferReasons(): Promise<readonly TransferReasonOption[] | null> {
  try {
    return await callOperation(transferReasonVocabulary, {});
  } catch {
    return null;
  }
}

/**
 * The transfer sheet over rows already read, in the order given — the CSV of a ranked, filtered
 * transfer table (`docs/adr/9034`): every matching transfer, in the planner's rank order, with the
 * run's provenance on every row (ADR 9120).
 */
export async function transferRequest(
  ordered: readonly TransferRow[], runId: string | null, sort: ExportRequest['perimeter']['sort'],
): Promise<ExportRequest> {
  const [context, report, reasons, parameters] = await Promise.all([
    readExportContext(runId), readTransfersReportIndex(runId), readTransferReasons(), readInSeasonRunParameters(runId),
  ]);
  return withParametersInForce(transferSheet(ordered, runId, sort, context, translateNow, report, reasons), parameters);
}

/** What a transfer file cites. None of it is in the metric register yet. */
const TRANSFER_MEASURES: readonly CitedMeasure[] = [
  {
    measure: 'transfer_net_benefit',
    definition:
      'Incremental full-price margin less lane cost, handling and donor run damage. A transfer is proposed only where this clears, and the lane cost is priced into the shared objective rather than netted afterwards.',
  },
];

/** The transfer sheet over rows and a context already read, in the order given. Pure. */
export function transferSheet(
  ordered: readonly TransferRow[], runId: string | null,
  sort: ExportRequest['perimeter']['sort'],
  context: ExportContext, translate: Translate,
  report: ReadonlyMap<string, TransferReportRow> = new Map(),
  /** The transfer reason vocabulary, for the reason's words in the workbook; `null` when unread. */
  reasons: readonly TransferReasonOption[] | null = null,
): ExportRequest {
  const reasonWords = new Map((reasons ?? []).map((option) => [option.code, option.label.text]));
  const rows: ExportCell[][] = ordered.map((row) => [
    { kind: 'text', value: row.transferId },
    { kind: 'text', value: row.donorStoreKey },
    { kind: 'text', value: row.recipientStoreKey },
    { kind: 'text', value: row.articleKey },
    ...sizeExportCells(row.size),
    { kind: 'integer', raw: row.units },
    { kind: 'label', value: row.purpose, code: row.purpose },
    { kind: 'money', raw: row.laneCost, currency: row.currencyCode },
    { kind: 'money', raw: row.runDamage, currency: row.currencyCode },
    { kind: 'money', raw: row.netBenefit, currency: row.currencyCode },
    // ADR 6031: `pending` where no disposition is recorded — never a default decision. The
    // workbook says the decision in the grid's words and leaves a pending line's reason and time
    // blank (DEC-104, usability audit E1), as the replenishment file does; the CSV keeps the codes.
    ...dispositionCells({
      disposition: row.disposition, disposition_reason_code: row.dispositionReasonCode, disposition_decided_at: row.dispositionDecidedAt,
      disposition_actor: row.dispositionActor ?? null, disposition_actor_name: row.dispositionActorName ?? null,
      disposition_actor_name_absent_reason: row.dispositionActorNameAbsentReason ?? null,
    }),
    { kind: 'text', value: translate(toPurposeKey(row.purpose)) },
    ...transferAttributeCells(report.get(row.transferId)),
  ]);
  const columns: ExportColumn[] = [
    { key: 'transfer_id', headerKey: 'inseason.column.transferId', descriptionKey: 'inseason.columnGuide.transferWhy.transfer_id', kind: 'text' },
    { key: 'donor', headerKey: 'inseason.column.donor', descriptionKey: 'inseason.columnGuide.transferWhy.donor_store_key', kind: 'text' },
    { key: 'recipient', headerKey: 'inseason.column.recipient', descriptionKey: 'inseason.columnGuide.transferWhy.recipient_store_key', kind: 'text' },
    { key: 'article_key', headerKey: 'inseason.column.article', descriptionKey: 'inseason.columnGuide.transferWhy.article_key', kind: 'text' },
    ...SIZE_EXPORT_COLUMNS,
    { key: 'units', headerKey: 'inseason.column.units', descriptionKey: 'inseason.columnGuide.transferWhy.units', kind: 'integer', unit: 'pairs' },
    // The purpose in the screen's words in the workbook; a purpose this build cannot name, as sent.
    {
      key: 'purpose', headerKey: 'inseason.column.purpose', descriptionKey: 'inseason.columnGuide.transferWhy.purpose', kind: 'label',
      display: catalogueWords((code) => (toPurposeKey(code) === UNCLASSIFIED_PURPOSE_KEY ? null : toPurposeKey(code))),
    },
    { key: 'lane_cost', headerKey: 'inseason.column.laneCost', descriptionKey: 'inseason.columnGuide.transferWhy.lane_cost', kind: 'money', currency: 'COP', polarity: 'lower-is-better' },
    { key: 'run_damage', headerKey: 'inseason.column.runDamage', descriptionKey: 'inseason.columnGuide.transferWhy.run_damage', kind: 'money', currency: 'COP', polarity: 'lower-is-better' },
    { key: 'net_benefit', headerKey: 'inseason.column.netBenefit', descriptionKey: 'inseason.columnGuide.transferWhy.net_benefit', kind: 'money', currency: 'COP', polarity: 'higher-is-better' },
    ...DISPOSITION_COLUMNS.map((column) => (column.key === 'disposition_reason_code'
      ? { ...column, display: (code: string): string => reasonWords.get(code) ?? code }
      : column)),
    { key: 'purpose_label', headerKey: 'inseason.exportDrivers.column.purposeLabel', descriptionKey: 'inseason.columnGuide.transferWhyWorkbook.purpose_label', kind: 'text' },
    ...TRANSFER_ATTRIBUTE_COLUMNS,
  ];
  return withRunProvenance({
    id: 'inseason.rebalancing.table',
    titleKey: 'inseason.rebalancing.title',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(runFilter(runId), []),
    perimeter: {
      filters: runFilter(runId),
      // The registered export: purpose first, then the netting within it — the grids are one
      // per published action type, and a file sorted only by net benefit would interleave the
      // three types the planner reads separately. The table's own CSV is its view, as sorted.
      sort,
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  }, context, TRANSFER_MEASURES);
}

// ── the «every why field» sheet (the transfer-explanations report) ─────────────────────────────

export { TRANSFER_WHY_FIELDS, transferWhyCells };
export type { TransferWhyField } from './transfer-why-fields';

/**
 * The report's own filters for the planner's ranked-cap filters (`transferRanking`), where the
 * endpoint has one that means the same thing. `purpose` is exact on both sides. A recorded
 * decision is `disposition`; «pending» is NOT sent, because the list's «pending» is every undecided
 * line while the report splits those into `pending` and `superseded` — on a superseded run the
 * report's `pending` is empty. Donor and recipient are substring searches on the list and the
 * report's `store_key` is an exact match on either door, so neither is sent. What is not sent is
 * applied on the rows (`passesFilters`, the list's own predicate). Pure.
 */
export function transferWhyQuery(ranked: RankedScope): { purpose?: string; disposition?: string } {
  const { purpose, decision } = ranked.filters;
  return {
    ...(purpose === undefined ? {} : { purpose }),
    ...(decision === undefined || decision === PENDING ? {} : { disposition: decision }),
  };
}

/** A report row as the list's row, so the list's ranking and filters apply to it unchanged. */
function asTransferRow(row: TransferExplanationReportRow): TransferRow {
  return {
    transferId: row.transfer_id,
    donorStoreKey: row.donor_store_key,
    recipientStoreKey: row.recipient_store_key,
    articleKey: row.article_key,
    sizeKey: row.size_key,
    size: sizeName(row),
    units: row.units,
    purpose: row.purpose,
    laneCost: row.lane_cost,
    runDamage: row.run_damage,
    netBenefit: row.net_benefit,
    currencyCode: row.currency_code,
    disposition: row.disposition ?? null,
    dispositionReasonCode: row.disposition_reason_code ?? null,
    dispositionDecidedAt: row.disposition_decided_at ?? null,
    adjustedUnits: row.adjusted_units ?? null,
    supersededByRunId: row.superseded_by_run_id ?? null,
    supersededAt: row.superseded_at ?? null,
  };
}

/**
 * The report's rows that pass the planner's filters, in the planner's rank order — the list's own
 * `rankRows` over the list's own `transferRanking`, so the file is the list's filtered set, every
 * row of it and not the fifty drawn. Pure.
 */
export function rankTransferWhyRows(
  items: readonly TransferExplanationReportRow[], ranked: RankedScope, translate: Translate,
  /** The articles the platform places in the family filtered by (`list_transfers?department_family=`). */
  familyArticles: ReadonlySet<string> | null = null,
): readonly TransferExplanationReportRow[] {
  const byRow = new Map<TransferRow, TransferExplanationReportRow>();
  const rows = items.map((item) => {
    const row = asTransferRow(item);
    byRow.set(row, item);
    return row;
  });
  const config = transferRanking((key) => translate(key), transferFamilyFilter(ranked.filters['family'] ?? null, familyArticles));
  return rankRows(rows, { ...config, cap: Number.MAX_SAFE_INTEGER }, ranked).filtered
    .map((row) => byRow.get(row))
    .filter((item): item is TransferExplanationReportRow => item !== undefined);
}

/**
 * The family filter of the transfer ranking, answered by `articles` — the articles the platform's
 * `list_transfers?department_family=` returned. No row carries its family: an article outside the
 * set, or a set not read, never matches. Pure.
 */
export function transferFamilyFilter(family: string | null, articles: ReadonlySet<string> | null): RankFilter<TransferRow> {
  return {
    id: 'family', labelKey: 'inseason.queueFilter.family', kind: 'select',
    value: (row) => (family !== null && articles !== null && articles.has(row.articleKey) ? family : null),
  };
}

/** The perimeter the file states: the run, then each filter the planner set, as set. Pure. */
function transferWhyFilters(runId: string | null, ranked: RankedScope): readonly ProvenanceFilter[] {
  return [
    ...runFilter(runId),
    ...Object.entries(ranked.filters).map(([field, value]): ProvenanceFilter => ({
      field, operator: field === 'donor' || field === 'recipient' || field === 'article' ? 'contains' : 'equals', value,
    })),
  ];
}

/** What the Excel workbook of the Why export joins to the explanation rows (DEC-104). */
export interface TransferWhyJoin {
  /** The run's transfers report — names, stock before and after, sales, risk — every page. */
  readonly report: readonly TransferReportRow[];
  /** The transfer reason vocabulary, or `null` when it could not be read. */
  readonly reasons: readonly TransferReasonOption[] | null;
  /** Who decided each transfer, by `transfer_id` (`GET /inseason/transfers`); absent: not read. */
  readonly deciders?: TransferDeciders;
  /**
   * The run's moves a weekly cap held back (owner ruling 2026-10-30, `./held-back-moves.ts`): a sheet of
   * their own after the transfers' two. Absent: no such sheet.
   */
  readonly heldBack?: HeldBackMoves;
}

/** The «every why field» file's own columns: one per report field, in its order. `currency` labels the amounts. Pure. */
export function transferWhyColumns(currency: string): ExportColumn[] {
  const fields = TRANSFER_WHY_FIELDS.map(({ field, kind, headerKey, descriptionKey, unit, polarity }): ExportColumn => ({
    key: field,
    headerKey,
    descriptionKey,
    kind: kind === 'boolean' ? 'text' : kind,
    ...(unit === undefined ? {} : { unit }),
    ...(kind === 'money' ? { currency } : {}),
    ...(polarity === undefined ? {} : { polarity }),
  }));
  // «Decided by», beside «Decided at», as every other decision file has it (issue 249).
  return withDecidedBy(fields, TRANSFER_WHY_DECIDED_BY_COLUMN);
}

/**
 * Compacting › Why's column guide (`docs/adr/9045`): the report's fields and the run's provenance
 * columns, exactly the CSV's — built from the definitions, nothing read. An amount's currency is
 * each row's own, in the column beside it, so the guide names none.
 */
export const TRANSFER_WHY_COLUMN_GUIDE: ColumnGuideSource = {
  id: 'inseason.transferWhy.table',
  titleKey: 'inseason.transferWhyWorkbook.file',
  columns: [...transferWhyColumns('COP'), ...PROVENANCE_COLUMNS],
};

/**
 * The «every why field» sheet over report rows already read and ordered. Pure.
 *
 * The CSV is the explanation report's own fields, one column each, codes as codes — the machine
 * file. With `join`, the request also carries the Excel workbook for people (DEC-104,
 * `docs/adr/9041`, `transfer-why-workbook.ts`): the same rows, laid out with names and words.
 */
export function transferWhySheet(
  ordered: readonly TransferExplanationReportRow[], runId: string | null, ranked: RankedScope,
  context: ExportContext, translate: Translate, join?: TransferWhyJoin,
): ExportRequest {
  const rows: ExportCell[][] = ordered.map((row) => withDecidedBy(transferWhyCells(row), transferDecidedByCell(row, join?.deciders)));
  const columns = transferWhyColumns(ordered[0]?.currency_code ?? 'COP');
  const { option, direction } = resolveRank(ranked, transferRanking((key) => translate(key)));
  const filters = transferWhyFilters(runId, ranked);
  return withRunProvenance({
    id: 'inseason.transferWhy.table',
    titleKey: 'inseason.transferWhyExport.title',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, []),
    perimeter: {
      filters,
      sort: option === null ? [] : [{ key: option.id, direction }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
    ...(join === undefined ? {} : {
      workbook: {
        titleKey: 'inseason.transferWhyWorkbook.file',
        sheets: [
          ...transferWhyWorkbook(ordered, join.report, join.reasons, translate, join.deciders),
          ...(join.heldBack === undefined ? [] : [heldBackSheet(join.heldBack)]),
        ],
      },
    }),
  }, context, TRANSFER_MEASURES);
}

/**
 * What the workbook joins, read beside the explanations. Never rejects: a transfers report that
 * cannot be read leaves every line unjoined — kept, names «n.d.», «In the transfers report: No» in
 * the «Tecnico» sheet — and an unread vocabulary leaves each decision reason as its code.
 */
async function readTransferWhyJoin(runId: string | null): Promise<TransferWhyJoin> {
  if (runId === null) return { report: [], reasons: null, heldBack: await readHeldBackMoves(null) };
  const [report, reasons, heldBack] = await Promise.allSettled([
    collectFresh(transfersReport, { path: { run_id: runId } }, i18next.language),
    callOperation(transferReasonVocabulary, {}),
    readHeldBackMoves(runId),
  ]);
  return {
    report: report.status === 'fulfilled' ? report.value : [],
    reasons: reasons.status === 'fulfilled' ? reasons.value : null,
    heldBack: heldBack.status === 'fulfilled' ? heldBack.value : { state: 'absent', runId, reason: 'unread' },
  };
}

/**
 * The CSV of the run's moves a weekly cap held back — the same rows and columns as the Why workbook's
 * sheet, codes as codes, with the run's provenance on every row (owner ruling 2026-10-30). Pure.
 */
export function heldBackMovesRequest(found: HeldBackMoves, context: ExportContext): ExportRequest {
  const { columns, rows } = heldBackTable(found);
  const filters: readonly ProvenanceFilter[] = [
    ...runFilter(found.runId),
    { field: 'reason_code', operator: 'in', value: HELD_BACK_REASONS.join(',') },
  ];
  return withRunProvenance({
    id: HELD_BACK_EXPORT_ID,
    titleKey: HELD_BACK_TITLE_KEY,
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, []),
    perimeter: {
      filters,
      sort: [{ key: 'stage', direction: 'asc' }, { key: 'store_key', direction: 'asc' }, { key: 'article_key', direction: 'asc' }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  }, context, []);
}

/** The held-back moves' CSV for the run in the URL — the button beside the Why file, and the census's resolver. */
export async function heldBackMovesExport(given?: ExportScope): Promise<ExportRequest> {
  const { runId } = scope(given);
  const [found, context] = await Promise.all([readHeldBackMoves(runId), readExportContext(runId)]);
  return heldBackMovesRequest(found, context);
}

/**
 * The «every why field» file for a run and the planner's ranking and filters: every page of the
 * transfer-explanations report (`collectPages`, 1,000 rows a page), narrowed by the filters the
 * endpoint carries and then by the list's own predicate, in the list's rank order.
 */
export async function transferWhyRequest(runId: string | null, ranked: RankedScope): Promise<ExportRequest> {
  const family = ranked.filters['family'] ?? null;
  const [items, join, context, inFamily, parameters] = await Promise.all([
    runId === null
      ? Promise.resolve([])
      : collectPages(transferExplanationsReport, { path: { run_id: runId }, query: transferWhyQuery(ranked) }),
    readTransferWhyJoin(runId),
    readExportContext(runId),
    // The report takes no family: the family's articles come from the transfers read that does
    // (usability audit C4), so the file is the list's family and never the whole run. The same
    // read names who decided each transfer, which the report does not (issue 249): with no family
    // it is the run's every transfer, and a failure then only leaves «Decided by» not reported.
    runId === null
      ? Promise.resolve(null)
      : family === null
        ? collectPages(listTransfers, { query: { run_id: runId } }).catch(() => null)
        : collectPages(listTransfers, { query: { run_id: runId, department_family: family } }),
    readInSeasonRunParameters(runId),
  ]);
  const articles = inFamily === null || family === null ? null : new Set(inFamily.map((row) => row.article_key));
  const withDeciders: TransferWhyJoin = inFamily === null ? join : { ...join, deciders: transferDecidersOf(inFamily) };
  // bata-reqs ADR 9001 decision 5: after the workbook's own sheets, the run's parameters in force.
  return withParametersInForce(
    transferWhySheet(rankTransferWhyRows(items, ranked, translateNow, articles), runId, ranked, context, translateNow, withDeciders),
    parameters,
  );
}

/** §4.5 — the Why page's registered export: the run and the ranking and filters its URL states. */
export async function transferWhyExport(given?: ExportScope): Promise<ExportRequest> {
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const { runId } = readScope(search);
  return transferWhyRequest(runId, readRankedScope(search, transferRanking((key) => translateNow(key), transferFamilyFilter(null, null))));
}

/** Gate D2's two states per market, in the words the Cost to serve table draws them in. */
const D2_STATE_WORDS: Readonly<Record<string, string>> = {
  measured: 'inseason.measurability.measured',
  unmeasured: 'inseason.measurability.unmeasured',
  enabled: 'inseason.gate.enabled',
  held: 'inseason.gate.held',
};

/** A platform code as a label cell; no code is `missing`, never a made-up code. */
function codeOrMissing(code: string | null | undefined): ExportCell {
  return code === null || code === undefined || code.trim() === ''
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'label', value: code, code };
}

/** Why a market is held: empty on an enabled market, which has no hold reason; the stored code on a held one. */
function holdReasonCell(held: boolean, code: string | null | undefined): ExportCell {
  return held ? codeOrMissing(code) : { kind: 'text', value: '' };
}

/**
 * Gate D2's per-country evidence, exported as the record it is decided from.
 *
 * Measurability and enablement are separate columns here for the same reason they are
 * separate columns on screen: a spreadsheet that showed only `transfers_enabled` would
 * let a market that D2 could not be measured in be counted as a market that failed it,
 * and the uncalibrated lane count is the number that tells them apart.
 */
export async function costToServeExport(): Promise<ExportRequest> {
  const { asOf } = scope();
  const items = await collectPages(
    listD2Evidence,
    { query: asOf === null ? {} : { as_of: asOf } },
  );
  // A dated read of every market's assessment, not one run's output: the sheet says so (ADR 9001).
  return withParametersInForce(costToServeRequest(items, asOf), NO_RUN);
}

/** The Cost to serve file over assessments already read (the dated read's rows, newest first per market). Pure. */
export function costToServeRequest(items: readonly GateD2EvidenceRow[], asOf: string | null): ExportRequest {
  // The platform's own codes for the CSV (the column guide promises "the code stored on the
  // assessment"): the view carries the catalogue key the screen draws, and the newest assessment of
  // each market is the row the view was made from (`toD2MarketViews`).
  const newest = new Map<string, GateD2EvidenceRow>();
  for (const item of items) if (!newest.has(item.country_key)) newest.set(item.country_key, item);
  const rows: ExportCell[][] = toD2MarketViews(items).map((row) => [
    { kind: 'text', value: row.countryKey },
    { kind: 'date', iso: row.assessedOn },
    { kind: 'label', value: row.measurability, code: row.measurability },
    { kind: 'integer', raw: row.uncalibratedLanes },
    { kind: 'integer', raw: row.candidateLanes },
    { kind: 'integer', raw: row.clearingLanes },
    // ADR 6050: undefined, never 0 — the reason travels in `measures_absent_reason`.
    row.clearingShare === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'percent', raw: row.clearingShare },
    { kind: 'percent', raw: row.minimumClearingShare },
    row.medianNetBenefit === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: row.medianNetBenefit },
    { kind: 'label', value: row.enablement, code: row.enablement },
    holdReasonCell(row.enablement === 'held', newest.get(row.countryKey)?.hold_reason_code),
    { kind: 'text', value: row.calibrationVersionId },
    row.measuresAbsentKey === null
      ? { kind: 'text', value: '' }
      : codeOrMissing(newest.get(row.countryKey)?.measures_absent_reason),
  ]);
  const filters: readonly ProvenanceFilter[] = asOf === null
    ? [{ field: 'as_of', operator: 'service-default', value: '' }]
    : [{ field: 'as_of', operator: 'equals', value: asOf }];
  return {
    id: 'inseason.costToServe.table',
    titleKey: 'inseason.costToServe.title',
    columns: [
      { key: 'country_key', headerKey: 'inseason.column.country', descriptionKey: 'inseason.help.costToServePanel.colCountry', kind: 'text' },
      { key: 'assessed_on', headerKey: 'inseason.column.assessedOn', descriptionKey: 'inseason.columnGuide.costToServe.assessedOn', kind: 'date' },
      {
        key: 'measurability', headerKey: 'inseason.column.measurability', descriptionKey: 'inseason.columnGuide.costToServe.measurability', kind: 'label',
        display: catalogueWords((code) => D2_STATE_WORDS[code] ?? null),
      },
      { key: 'uncalibrated_lanes', headerKey: 'inseason.column.uncalibratedLanes', descriptionKey: 'inseason.columnGuide.costToServe.uncalibratedLanes', kind: 'integer' },
      { key: 'candidate_lanes', headerKey: 'inseason.column.candidateLanes', descriptionKey: 'inseason.help.costToServePanel.colCandidate', kind: 'integer' },
      { key: 'clearing_lanes', headerKey: 'inseason.column.clearingLanes', descriptionKey: 'inseason.help.costToServePanel.colClearing', kind: 'integer' },
      { key: 'clearing_share', headerKey: 'inseason.column.clearingShare', descriptionKey: 'inseason.columnGuide.costToServe.clearingShare', kind: 'percent', polarity: 'higher-is-better' },
      { key: 'minimum_clearing_share', headerKey: 'inseason.column.minimumClearingShare', descriptionKey: 'inseason.help.costToServePanel.colThreshold', kind: 'percent' },
      { key: 'median_net_benefit', headerKey: 'inseason.column.medianNetBenefit', descriptionKey: 'inseason.columnGuide.costToServe.medianNetBenefit', kind: 'number', polarity: 'higher-is-better' },
      {
        key: 'transfers_enabled', headerKey: 'inseason.column.transfersEnabled', descriptionKey: 'inseason.help.costToServePanel.colEnabled', kind: 'label',
        display: catalogueWords((code) => D2_STATE_WORDS[code] ?? null),
      },
      // The stored code in the CSV, the screen's words in the Excel — the same words for a code
      // this build does not know as the screen's «unclassified».
      { key: 'hold_reason', headerKey: 'inseason.column.holdReason', descriptionKey: 'inseason.columnGuide.costToServe.holdReason', kind: 'label', display: catalogueWords(toHoldReasonKey) },
      { key: 'calibration_version_id', headerKey: 'inseason.column.calibrationVersion', descriptionKey: 'inseason.columnGuide.costToServe.calibrationVersion', kind: 'text' },
      { key: 'measures_absent_reason', headerKey: 'inseason.column.measuresAbsentReason', descriptionKey: 'inseason.columnGuide.costToServe.measuresAbsentReason', kind: 'label', display: catalogueWords(toMeasuresAbsentKey) },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, [
      {
        measure: 'transfer_clearing_share',
        definition:
          'Share of candidate lanes whose net benefit is positive. A share rather than a mean, because a mean can be positive on a handful of dense-city pairs while most lanes lose money — and it is the marginal lane that decides whether the capability is worth operating. A market with uncalibrated lanes is UNMEASURED, which is not the same finding as a market that did not clear.',
      },
    ]),
    perimeter: {
      filters,
      sort: [{ key: 'country_key', direction: 'asc' }],
      visibleColumns: ['country_key', 'assessed_on', 'measurability', 'uncalibrated_lanes', 'candidate_lanes', 'clearing_lanes', 'clearing_share', 'minimum_clearing_share', 'median_net_benefit', 'transfers_enabled', 'hold_reason', 'calibration_version_id', 'measures_absent_reason'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * §14 §5 O4/O5 — the delivery state of approved commands, uncollapsed.
 *
 * **This is the download half of "approved recommendations must be visible AND
 * downloadable".** It reads `GET /inseason/write-back` through `collectPages`, which is
 * the same operation through the same transport that `WriteBackScreen` reads through
 * `useApiCollection` — so the spreadsheet and the screen cannot disagree about what the
 * estate holds, and the file is correct the moment the first command is minted rather
 * than the day somebody remembers to wire it.
 *
 * The state leaves as the target's own word. Test E10 forbids any surface — including an
 * export — rendering `dispatched`, `delivered` or `unknown` as sent or done, so there is
 * no derived "complete" column here and there will not be one.
 *
 * TWO CELLS THAT ARE NOT BOOLEANS, and were being written as if they were:
 *
 * `sla_elapsed` is three-valued. `null` means no SLA is configured for this target — see
 * `needsAttention` in `./api.ts`, corrected for exactly this in `0.18.0` — and a
 * truthiness test wrote `within` into that cell, certifying every command in the estate
 * as on time while nothing was being timed. A spreadsheet outlives the screen it was
 * taken from and is the artefact somebody quotes in a review, so this is the surface
 * where the collapse does the most damage. `missing` is the only cell kind that carries
 * the third state; `not-ranged` rather than `not-reported`, because no deadline was ever
 * set for this target — the question does not apply, as opposed to an answer that failed
 * to arrive.
 *
 * `approval_id` is flagged on screen when it is empty, because §14 test E5 makes an
 * unauthorised outbound command a release blocker rather than a blank. An empty string in
 * a CSV reads as nothing at all, which loses the finding precisely where a reviewer would
 * look for it, so the absence is stated instead.
 */
export async function writeBackExport(given?: ExportScope): Promise<ExportRequest> {
  // The log's filters on the link (`?f.…=`, usability audit C6) narrow the file as they narrow the
  // table, and the file says so; its own order is kept.
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const config = logRanking((key) => translateNow(key), (kind) => kind, (target) => target);
  const scoped = readRankedScope(search, config);
  // `?needsAttention=true` (ADR 9069): the file holds the commands the page lists, read narrowed by the platform.
  const items = (await collectPages(listWriteBack, { query: readNeedsAttention(search) ? { needs_attention: true } : {} }))
    .filter((row) => passesFilters(row, scoped, config));
  const request = writeBackRequest(items);
  const filters = rankedProvenanceFilters(config, scoped.filters);
  if (filters.length === 0) return request;
  const stated = request.provenance.filters;
  return {
    ...request,
    provenance: { ...request.provenance, filters: isAbsentProvenance(stated) ? stated : [...stated, ...filters] },
    perimeter: { ...request.perimeter, filters: [...request.perimeter.filters, ...filters] },
  };
}

/** The write-back log's sheet over rows already read (`docs/adr/9008`). */
export function writeBackRequest(items: readonly WriteBackState[]): ExportRequest {
  const rows: ExportCell[][] = items.map((row) => [
    { kind: 'text', value: row.command_id },
    row.approval_id === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'text', value: row.approval_id },
    { kind: 'label', value: row.subject_kind, code: row.subject_kind },
    { kind: 'label', value: row.target, code: row.target },
    { kind: 'label', value: row.state, code: row.state },
    row.reason_code === null || row.reason_code === undefined || row.reason_code === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'label', value: row.reason_code, code: row.reason_code },
    row.sla_elapsed === true
      ? { kind: 'label', value: 'elapsed', code: 'elapsed' }
      : row.sla_elapsed === false
        ? { kind: 'label', value: 'within', code: 'within' }
        : { kind: 'missing', state: 'not-ranged' },
  ]);
  return {
    id: 'inseason.writeBack.table',
    titleKey: 'inseason.writeBack.title',
    columns: [
      { key: 'command_id', headerKey: 'inseason.column.commandId', descriptionKey: 'inseason.columnGuide.writeBack.commandId', kind: 'text' },
      { key: 'approval_id', headerKey: 'inseason.column.approvalId', descriptionKey: 'inseason.columnGuide.writeBack.approvalId', kind: 'text' },
      { key: 'subject_kind', headerKey: 'inseason.column.subjectKind', descriptionKey: 'inseason.columnGuide.writeBack.subjectKind', kind: 'label' },
      { key: 'target', headerKey: 'inseason.column.target', descriptionKey: 'inseason.columnGuide.writeBack.target', kind: 'label' },
      { key: 'delivery_state', headerKey: 'inseason.column.deliveryState', descriptionKey: 'inseason.columnGuide.writeBack.deliveryState', kind: 'label', display: DELIVERY_WORDS },
      { key: 'reason_code', headerKey: 'inseason.column.reasonCode', descriptionKey: 'inseason.columnGuide.writeBack.reasonCode', kind: 'label' },
      { key: 'sla', headerKey: 'inseason.column.sla', descriptionKey: 'inseason.columnGuide.writeBack.sla', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([], [
      {
        measure: 'command_delivery_state',
        definition:
          'The target system’s own acknowledgement state. Never collapsed: dispatched, delivered and unknown are not sent and are not done, and unknown is a real operational state with an owner.',
      },
      {
        measure: 'command_sla_elapsed',
        definition:
          'Whether the acknowledgement deadline for this target has passed. Three-valued: elapsed, within, or NOT RANGED where no SLA is configured for the target at all. An unconfigured deadline is not a command that is on time, and reading the blank as "within" would report the whole estate as within time while nothing was being measured.',
      },
    ]),
    perimeter: {
      filters: [],
      sort: [{ key: 'command_id', direction: 'asc' }],
      visibleColumns: ['command_id', 'approval_id', 'subject_kind', 'target', 'delivery_state', 'reason_code', 'sla'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The in-season exception queue, exported from the platform's only inbox. */
export async function exceptionQueueExport(): Promise<ExportRequest> {
  const items = await collectPages(foundationsListExceptions, { query: { open_only: true } });
  const rows: ExportCell[][] = toExceptions(items).map((exception, index) => [
    { kind: 'text', value: exception.id },
    { kind: 'label', value: exception.thresholdClass, code: exception.thresholdClass },
    { kind: 'text', value: exception.entityLabel },
    // The store and article by name beside the subject's code (usability audit B4).
    ...alertNameExportCells(items[index]!),
    { kind: 'text', value: exception.measure },
    exception.actual.kind === 'number'
      ? { kind: 'number', raw: exception.actual.value }
      : { kind: 'missing', state: 'not-reported' },
    exception.expected.kind === 'number'
      ? { kind: 'number', raw: exception.expected.value }
      : { kind: 'missing', state: 'not-reported' },
    { kind: 'date', iso: exception.detectedAt },
  ]);
  return {
    id: 'inseason.workbench.table',
    titleKey: 'inseason.workbench.title',
    columns: [
      { key: 'alert_id', headerKey: 'inseason.column.alertId', descriptionKey: 'inseason.columnGuide.workbench.alertId', kind: 'text' },
      { key: 'threshold_class', headerKey: 'inseason.column.thresholdClass', descriptionKey: 'inseason.columnGuide.workbench.thresholdClass', kind: 'label' },
      { key: 'entity', headerKey: 'inseason.column.entity', descriptionKey: 'inseason.columnGuide.workbench.entity', kind: 'text' },
      ...ALERT_NAME_EXPORT_COLUMNS,
      { key: 'measure', headerKey: 'inseason.column.measure', descriptionKey: 'inseason.columnGuide.workbench.measure', kind: 'text' },
      { key: 'actual', headerKey: 'inseason.column.actual', descriptionKey: 'inseason.columnGuide.workbench.actual', kind: 'number' },
      { key: 'expected', headerKey: 'inseason.column.expected', descriptionKey: 'inseason.columnGuide.workbench.expected', kind: 'number' },
      { key: 'detected_at', headerKey: 'inseason.column.detectedAt', descriptionKey: 'inseason.columnGuide.workbench.detectedAt', kind: 'date' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([{ field: 'open_only', operator: 'equals', value: 'true' }], [
      {
        measure: 'alert_raised',
        definition:
          'An item raised by the shared rule engine against a registry threshold. The queue is the only inbox (G1): nothing in this chapter emails, spreadsheets or relies on somebody noticing. The alert model publishes no lineage, so the register version, source batch and plan version are empty rather than plausible.',
      },
    ]),
    perimeter: {
      filters: [{ field: 'open_only', operator: 'equals', value: 'true' }],
      sort: [{ key: 'threshold_class', direction: 'asc' }],
      visibleColumns: ['alert_id', 'threshold_class', 'entity', ...ALERT_NAME_EXPORT_KEYS, 'measure', 'actual', 'expected', 'detected_at'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The cockpit's two weekly charts — price steps per effective week, and expected margin
 * recovery by the week each path STARTS (each path once), of one cycle run.
 *
 * Built from the same `provenance` and `runFilter` every export in this chapter uses, so the
 * file cites the run it was read over, and the empty fields stay honestly empty for the reason
 * `provenance` gives. The data is the chart's own: `KpiReportingTile` derives the rows from the
 * spec it draws, so the file and the bars are one value (ADR 9000).
 */
export function priceStepsByWeekChartExportMeta(
  runId: string,
  measure: 'steps' | 'recovery',
): ChartExportMeta {
  const filters = runFilter(runId);
  return {
    id: measure === 'steps' ? 'overview.markdown.stepsChart' : 'overview.markdown.recoveryChart',
    titleKey: measure === 'steps' ? 'overview.markdown.chartTitle' : 'overview.markdown.recoveryChartTitle',
    provenance: provenance(filters, [measure === 'steps'
      ? {
        measure: 'inseason.price_steps',
        definition: 'The number of recommended price steps in the run whose effective week is the row\'s week, as GET /inseason/price-actions publishes them for the run.',
      }
      : {
        measure: 'inseason.expected_margin_recovery',
        definition: 'Expected margin recovery by the week the path STARTS: each path (article x cluster in the run) counted once, in the effective week of its first price step, never split or repeated across its later steps. Zero in a week where no path starts. In the currency of those paths; absent where they span currencies. Expected, not realised: it is what the optimiser projects, measured against later.',
      }]),
    perimeter: {
      filters,
      sort: [{ key: 'effective_week', direction: 'asc' }],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The dimensions the Explanation view groups the fitted elasticities by. */
/** The dimension the Explanation view groups the fitted elasticities by — carried into the
 *  export's filters as `group_by`, so a file names the split it was taken at. */
export type ElasticityDimension = string;

/**
 * The measures an elasticity export cites. The coefficient's own definition, and the two
 * columns that decide how far to believe it, stated so a spreadsheet read without the screen
 * still says what a coefficient of -1.8 means.
 */
const ELASTICITY_MEASURES = [
  {
    measure: 'price_elasticity',
    definition: 'The fitted own-price elasticity of demand for the series: the percentage change in units sold for a 1% change in price, holding the rest of the fit constant. Normally negative. Published per option x cluster x channel for one model version.',
  },
  {
    measure: 'own_weight',
    definition: 'The share of the coefficient that comes from the series\' own history; the remainder is the parent (category) prior it was shrunk towards.',
  },
  {
    measure: 'within_sanity_band',
    definition: 'Whether the fit judged the coefficient plausible against the sanity band in force when it was fitted. Carried, never re-tested against today\'s band.',
  },
];

function modelVersionFilter(modelVersionId: string | null): readonly ProvenanceFilter[] {
  return modelVersionId === null
    ? [{ field: 'model_version_id', operator: 'unset', value: '' }]
    : [{ field: 'model_version_id', operator: 'equals', value: modelVersionId }];
}

/**
 * The Explanation view's registered export — every fitted series of the generation in force,
 * one row each, with the columns that decide how far to believe it.
 *
 * Reads the same two routes the view reads, in the same order: the generation, then its
 * estimates pinned to it. A resolver that followed "the latest fit" would export a different
 * generation from the one on screen the day a new fit landed.
 */
export async function elasticityExport(): Promise<ExportRequest> {
  const version = await callOperation(elasticityModelVersion, {}, {});
  const modelVersionId = version?.model_version_id ?? null;
  const items = modelVersionId === null
    ? []
    : await collectPages(listElasticityEstimates, { query: { model_version_id: modelVersionId } });
  return elasticityRowsExport(items, modelVersionId, []);
}

/**
 * The same file for a subset of the series — the rows the Explanation view's drill-down has
 * narrowed to, with the path that narrowed them stated as filters, so a spreadsheet taken three
 * levels down says which three levels (G2.4: the export is the view the planner is looking at).
 */
export function elasticityRowsExport(
  items: readonly ElasticityEstimate[],
  modelVersionId: string | null,
  drill: readonly ProvenanceFilter[],
): ExportRequest {
  const optional = (value: number | null | undefined): ExportCell => (value === null || value === undefined
    ? { kind: 'missing', state: 'not-reported' }
    : { kind: 'number', raw: value });
  const rows: ExportCell[][] = items.map((row) => [
    { kind: 'text', value: row.option_key },
    { kind: 'text', value: row.cluster_key },
    { kind: 'text', value: row.channel_key },
    { kind: 'text', value: row.parent_key },
    { kind: 'number', raw: row.coefficient },
    optional(row.standard_error),
    { kind: 'integer', raw: row.observations },
    { kind: 'number', raw: row.price_variation },
    { kind: 'percent', raw: row.own_weight },
    { kind: 'label', value: row.within_sanity_band ? 'within' : 'outside', code: row.within_sanity_band ? 'within' : 'outside' },
  ]);
  const filters = [...modelVersionFilter(modelVersionId), ...drill];
  return {
    id: 'inseason.elasticity.table',
    titleKey: 'inseason.explain.title',
    columns: [
      { key: 'option_key', headerKey: 'inseason.column.article', descriptionKey: 'inseason.columnGuide.elasticity.optionKey', kind: 'text' },
      { key: 'cluster_key', headerKey: 'inseason.column.cluster', descriptionKey: 'inseason.columnGuide.elasticity.clusterKey', kind: 'text' },
      { key: 'channel_key', headerKey: 'inseason.explain.column.channel', descriptionKey: 'inseason.columnGuide.elasticity.channelKey', kind: 'text' },
      { key: 'parent_key', headerKey: 'inseason.explain.column.category', descriptionKey: 'inseason.columnGuide.elasticity.parentKey', kind: 'text' },
      { key: 'coefficient', headerKey: 'inseason.elasticity.coefficient', descriptionKey: 'inseason.columnGuide.elasticity.coefficient', kind: 'number' },
      { key: 'standard_error', headerKey: 'inseason.elasticity.standardError', descriptionKey: 'inseason.columnGuide.elasticity.standardError', kind: 'number' },
      { key: 'observations', headerKey: 'inseason.elasticity.observations', descriptionKey: 'inseason.columnGuide.elasticity.observations', kind: 'integer' },
      { key: 'price_variation', headerKey: 'inseason.explain.column.priceVariation', descriptionKey: 'inseason.columnGuide.elasticity.priceVariation', kind: 'number' },
      { key: 'own_weight', headerKey: 'inseason.elasticity.ownWeight', descriptionKey: 'inseason.columnGuide.elasticity.ownWeight', kind: 'percent' },
      { key: 'within_sanity_band', headerKey: 'inseason.explain.column.band', descriptionKey: 'inseason.columnGuide.elasticity.band', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, ELASTICITY_MEASURES),
    perimeter: {
      filters,
      sort: [{ key: 'observations', direction: 'desc' }],
      visibleColumns: ['option_key', 'cluster_key', 'channel_key', 'parent_key', 'coefficient', 'standard_error', 'observations', 'price_variation', 'own_weight', 'within_sanity_band'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The group chart's export — the medians it draws, per the dimension chosen. */
export function elasticityGroupsChartExportMeta(
  modelVersionId: string | null,
  dimension: ElasticityDimension,
): ChartExportMeta {
  // The grouping is part of the perimeter, so it travels as a filter row rather than inside
  // the definition's prose.
  const filters: readonly ProvenanceFilter[] = [
    ...modelVersionFilter(modelVersionId),
    { field: 'group_by', operator: 'equals', value: dimension },
  ];
  return {
    id: 'inseason.elasticity.groups',
    titleKey: 'inseason.explain.groupsChartTitle',
    provenance: provenance(filters, [{
      measure: 'median_price_elasticity',
      definition: 'The median of the published coefficients of the fitted series in each group of the group_by filter, within the model version named. A description of the fit, computed over the rows the screen read; not a coefficient the fit published.',
    }]),
    perimeter: {
      filters,
      sort: [{ key: 'series', direction: 'desc' }],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The "over time" chart's export — one option's expected units by week, from its solved path. */
export function elasticityOverTimeExportMeta(current: InSeasonScope): ChartExportMeta {
  const filters = optionFilter(current);
  return {
    id: 'inseason.elasticity.overTime',
    titleKey: 'inseason.explain.overTimeChartTitle',
    provenance: provenance(filters, MARKDOWN_MEASURES),
    perimeter: {
      filters,
      sort: [{ key: 'week', direction: 'asc' }],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * Pending approvals' registered export — the run's price-action queue, the rows the page decides
 * on (client rule 3, 2026-09-30: the queue left the Output view for a page of its own). With no
 * run on the URL the read is not issued and the file records the unset run.
 */
export async function priceActionQueueExport(given?: ExportScope): Promise<ExportRequest> {
  // The same sheet as the run's Calendar, under this page's own id (issue 226: one id per page).
  return { ...(await priceActionExport(scope(given))), id: 'inseason.markdown.approvals' };
}

/** The rows one stock-position page holds — the panel reads one page, bounded on purpose. */
export const STOCK_PAGE = 200;

/**
 * The store and article the stock position is narrowed to — `?stock.store=` / `?stock.article=`,
 * sent as `list_stock_positions`' own exact `store_key` / `article_key` (usability audit C2). A
 * narrowed read is walked to its end: one store's or one article's positions, not the partition.
 */
export interface StockScope {
  readonly store: string | null;
  readonly article: string | null;
}

export const NO_STOCK_SCOPE: StockScope = { store: null, article: null };
export const STOCK_STORE_PARAM = 'stock.store';
export const STOCK_ARTICLE_PARAM = 'stock.article';

/** The scope a search string states. Pure. */
export function readStockScope(search: string): StockScope {
  const params = new URLSearchParams(search);
  const read = (key: string): string | null => {
    const value = params.get(key)?.trim() ?? '';
    return value === '' ? null : value;
  };
  return { store: read(STOCK_STORE_PARAM), article: read(STOCK_ARTICLE_PARAM) };
}

/** `search` with the scope written (an unset dimension removed), every other key kept. Pure. */
export function writeStockScope(search: string, next: StockScope): string {
  const params = new URLSearchParams(search);
  for (const [key, value] of [[STOCK_STORE_PARAM, next.store], [STOCK_ARTICLE_PARAM, next.article]] as const) {
    if (value === null || value.trim() === '') params.delete(key);
    else params.set(key, value.trim());
  }
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/** Whether the scope narrows the read. Pure. */
export function stockScoped(scope: StockScope): boolean {
  return scope.store !== null || scope.article !== null;
}

/** The read's query for a run and a scope: the scope's dimensions only. Pure. */
export function stockQuery(runId: string, scope: StockScope): { run_id: string; store_key?: string; article_key?: string } {
  return {
    run_id: runId,
    ...(scope.store === null ? {} : { store_key: scope.store }),
    ...(scope.article === null ? {} : { article_key: scope.article }),
  };
}

/**
 * The stock-position view's registered export — what the panel reads, as `stockPositionsRequest`
 * sheets it: the first page of the run, or every row of the store or article the URL narrows it
 * to. No run, no read.
 */
export async function stockPositionExport(given?: ExportScope): Promise<ExportRequest> {
  const { runId } = scope(given);
  const narrowed = readStockScope(given?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  if (runId === null) return stockPositionsRequest([], runId, STOCK_PAGE);
  if (stockScoped(narrowed)) {
    return stockPositionsRequest(await collectPages(listStockPositions, { query: stockQuery(runId, narrowed) }), runId, null, null, narrowed);
  }
  const items = (await callOperation(listStockPositions, { query: { run_id: runId, limit: STOCK_PAGE } }, {})).items;
  return stockPositionsRequest(items, runId, STOCK_PAGE);
}

/**
 * The saved-configurations view's registered export — the team's active configurations of the
 * markdown question, each at its current version, narrowed to the URL's market where it names
 * one (the panel's own default list). The saved parameters leave as the JSON they are stored as:
 * a configuration is a question, and flattening it into columns would choose which of its
 * parameters matter.
 */
export async function runConfigurationsExport(given?: ExportScope): Promise<ExportRequest> {
  const params = new URLSearchParams(given?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  const market = params.get(MARKET_PARAMETER)?.trim() ?? '';
  const items = await collectPages(listRunConfigurations, {
    query: {
      surface: RUN_CONFIGURATION_SURFACE,
      mine: false,
      include_archived: false,
      ...(market === '' ? {} : { country_key: market }),
    },
  });
  const rows: ExportCell[][] = items.map((row) => [
    { kind: 'text', value: row.current_version.name },
    { kind: 'text', value: row.country_key },
    { kind: 'number', raw: row.current_version.version },
    { kind: 'text', value: row.current_version.recorded_at },
    { kind: 'label', value: row.owned_by_caller === true ? 'mine' : 'team', code: row.owned_by_caller === true ? 'mine' : 'team' },
    { kind: 'text', value: JSON.stringify(row.current_version.parameters) },
  ]);
  const filters: readonly ProvenanceFilter[] = [
    { field: 'surface', operator: 'equals', value: RUN_CONFIGURATION_SURFACE },
    { field: 'include_archived', operator: 'equals', value: 'false' },
    market === ''
      ? { field: 'country_key', operator: 'unset', value: '' }
      : { field: 'country_key', operator: 'equals', value: market },
  ];
  return {
    id: 'inseason.markdown.configurations',
    titleKey: 'inseason.runConfig.title',
    columns: [
      { key: 'name', headerKey: 'inseason.runConfig.column.name', descriptionKey: 'inseason.columnGuide.runConfig.name', kind: 'text' },
      { key: 'country_key', headerKey: 'inseason.runConfig.column.market', descriptionKey: 'inseason.columnGuide.runConfig.market', kind: 'text' },
      { key: 'version', headerKey: 'inseason.runConfig.column.version', descriptionKey: 'inseason.columnGuide.runConfig.version', kind: 'number' },
      { key: 'recorded_at', headerKey: 'inseason.runConfig.column.savedAt', descriptionKey: 'inseason.columnGuide.runConfig.savedAt', kind: 'text' },
      { key: 'owner', headerKey: 'inseason.runConfig.column.owner', descriptionKey: 'inseason.columnGuide.runConfig.owner', kind: 'label' },
      { key: 'parameters', headerKey: 'inseason.runConfig.column.parameters', descriptionKey: 'inseason.columnGuide.runConfig.parameters', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, [{
      measure: 'run_configuration',
      definition: 'A saved markdown question at its current version: its name, market, version, when it was saved, whose it is, and the parameters it poses. The cycle run and the as-at date are not part of a configuration.',
    }]),
    perimeter: {
      filters,
      sort: [],
      visibleColumns: ['name', 'country_key', 'version', 'recorded_at', 'owner', 'parameters'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * One saved configuration's versions — the configuration drill's table
 * (`/inseason/markdown/configurations/:configurationId`, issue 226). Every version, newest first, as
 * saved: versions are immutable, and the parameters leave as the JSON they are stored as.
 */
export async function runConfigurationVersionsExport(given?: ExportScope): Promise<ExportRequest> {
  const configurationId = readConfigurationDrill(given?.path ?? (typeof window === 'undefined' ? '' : window.location.pathname));
  const items = configurationId === null
    ? []
    : await collectPages(listRunConfigurationVersions, { path: { configuration_id: configurationId } });
  const rows: ExportCell[][] = items.map((row) => [
    { kind: 'number', raw: row.version },
    { kind: 'text', value: row.name },
    { kind: 'text', value: row.recorded_at },
    row.note === null || row.note === undefined || row.note === '' ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value: row.note },
    { kind: 'text', value: JSON.stringify(row.parameters) },
  ]);
  const filters: readonly ProvenanceFilter[] = configurationId === null
    ? [{ field: 'configuration_id', operator: 'unset', value: '' }]
    : [{ field: 'configuration_id', operator: 'equals', value: configurationId }];
  return {
    id: 'inseason.markdown.configurationVersions',
    titleKey: 'inseason.runConfig.versions.title',
    columns: [
      { key: 'version', headerKey: 'inseason.runConfig.column.version', descriptionKey: 'inseason.columnGuide.runConfig.version', kind: 'number' },
      { key: 'name', headerKey: 'inseason.runConfig.column.name', descriptionKey: 'inseason.columnGuide.runConfig.name', kind: 'text' },
      { key: 'recorded_at', headerKey: 'inseason.runConfig.column.savedAt', descriptionKey: 'inseason.columnGuide.runConfig.savedAt', kind: 'text' },
      { key: 'note', headerKey: 'inseason.runConfig.column.note', descriptionKey: 'inseason.columnGuide.runConfig.note', kind: 'text' },
      { key: 'parameters', headerKey: 'inseason.runConfig.column.parameters', descriptionKey: 'inseason.columnGuide.runConfig.parameters', kind: 'text' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, [{
      measure: 'run_configuration_version',
      definition: 'Every saved version of one markdown configuration, newest first, exactly as saved: its number, name, when it was saved, its note, and the parameters it poses. Versions are immutable; none is overwritten.',
    }]),
    perimeter: {
      filters,
      sort: [{ key: 'version', direction: 'desc' }],
      visibleColumns: ['version', 'name', 'recorded_at', 'note', 'parameters'],
      drillPath: configurationId === null ? [] : [configurationId],
      densityMode: 'compact',
    },
  };
}

/**
 * Two saved versions, parameter by parameter — the comparison drill's table
 * (`/inseason/markdown/configurations/compare?a=…&b=…`, issue 226). What each version ASKED, verbatim,
 * and whether the two strings are the same; no result is compared, because none is stored.
 */
export async function runConfigurationComparisonExport(given?: ExportScope): Promise<ExportRequest> {
  const { a, b } = readComparePicks(given?.search ?? (typeof window === 'undefined' ? '' : window.location.search));
  const sides = a === null || b === null
    ? null
    : await Promise.all([a, b].map((pick) => callOperation(getRunConfigurationVersion, {
      path: { configuration_id: pick.configurationId, version: pick.version },
    })));
  const missing: ExportCell = { kind: 'missing', state: 'not-reported' };
  const rows: ExportCell[][] = sides === null ? [] : compareParameters(sides[0]!.parameters, sides[1]!.parameters).map((row) => [
    { kind: 'label', value: row.name, code: row.name },
    row.first === null ? missing : { kind: 'text', value: row.first },
    row.second === null ? missing : { kind: 'text', value: row.second },
    { kind: 'label', value: row.same ? 'same' : 'different', code: row.same ? 'same' : 'different' },
  ]);
  const pickText = (pick: typeof a): string => (pick === null ? '' : `${pick.configurationId}:${pick.version}`);
  const filters: readonly ProvenanceFilter[] = [
    a === null ? { field: 'a', operator: 'unset', value: '' } : { field: 'a', operator: 'equals', value: pickText(a) },
    b === null ? { field: 'b', operator: 'unset', value: '' } : { field: 'b', operator: 'equals', value: pickText(b) },
  ];
  return {
    id: 'inseason.markdown.configurationComparison',
    titleKey: 'inseason.runConfig.compare.title',
    columns: [
      { key: 'parameter', headerKey: 'inseason.runConfig.compare.parameter', descriptionKey: 'inseason.columnGuide.runConfig.compareParameter', kind: 'label' },
      { key: 'first', headerKey: 'inseason.runConfig.compare.first', descriptionKey: 'inseason.columnGuide.runConfig.compareFirst', kind: 'text' },
      { key: 'second', headerKey: 'inseason.runConfig.compare.second', descriptionKey: 'inseason.columnGuide.runConfig.compareSecond', kind: 'text' },
      { key: 'verdict', headerKey: 'inseason.runConfig.compare.verdict', descriptionKey: 'inseason.columnGuide.runConfig.compareVerdict', kind: 'label' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, [{
      measure: 'run_configuration_comparison',
      definition: 'Each parameter either of two saved markdown configuration versions states, with the value each saved, verbatim. "same" is character-for-character equality of the two values; "different" otherwise, including when only one states it. Questions are compared, not results.',
    }]),
    perimeter: {
      filters,
      sort: [],
      visibleColumns: ['parameter', 'first', 'second', 'verdict'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The stock positions the markdown output's stock panel READ — one page, bounded on purpose
 * (`STOCK_PAGE`), and the file says so: `limit` is a filter row, not a silence. The panel
 * draws fifty of the page and this sheet is the page (`docs/adr/9008`); a position the page
 * did not reach is in neither, and the panel's own "more rows" note says so.
 *
 * Every measure is nullable on the wire and leaves as `missing`, never `0`: an unreported
 * on-hand is not an empty door, and a null cover is zero demand, not infinite cover.
 */
export function stockPositionsRequest(
  items: readonly StockPositionSummary[], runId: string | null, limit: number | null,
  /** The run's names (`run-names.tsx`), where the screen shows them: the file then carries them too. */
  names: RunNames | null = null,
  /** The store and article the read was narrowed to (usability audit C2): every page of it, no limit. */
  scope: StockScope = NO_STOCK_SCOPE,
): ExportRequest {
  const measure = (value: number | null): ExportCell => (
    value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: value }
  );
  const rows: ExportCell[][] = items.map((row) => [
    { kind: 'text', value: row.store_key },
    { kind: 'text', value: row.article_key },
    ...(names === null ? [] : nameExportCells([names.store(row.store_key), names.article(row.article_key)])),
    ...sizeExportCells(sizeName(row)),
    { kind: 'label', value: row.position_state, code: row.position_state },
    measure(row.on_hand_units),
    measure(row.committed_units),
    measure(row.weekly_demand),
    measure(row.weeks_of_cover),
    row.bound_by === null || row.bound_by === ''
      ? { kind: 'missing', state: 'not-reported' }
      : { kind: 'label', value: row.bound_by, code: row.bound_by },
  ]);
  const filters: readonly ProvenanceFilter[] = [
    ...runFilter(runId),
    ...(scope.store === null ? [] : [{ field: 'store_key', operator: 'equals', value: scope.store }]),
    ...(scope.article === null ? [] : [{ field: 'article_key', operator: 'equals', value: scope.article }]),
    ...(limit === null ? [] : [{ field: 'limit', operator: 'equals', value: String(limit) }]),
  ];
  return {
    id: 'inseason.markdown.stock',
    titleKey: 'inseason.stock.title',
    columns: [
      { key: 'store_key', headerKey: 'inseason.column.store', descriptionKey: 'inseason.columnGuide.stockPosition.storeKey', kind: 'text' },
      { key: 'article_key', headerKey: 'inseason.column.article', descriptionKey: 'inseason.columnGuide.stockPosition.articleKey', kind: 'text' },
      ...(names === null ? [] : nameExportColumns(['store', 'article'])),
      ...SIZE_EXPORT_COLUMNS,
      { key: 'position_state', headerKey: 'inseason.column.positionState', descriptionKey: 'inseason.columnGuide.stockPosition.positionState', kind: 'label' },
      { key: 'on_hand_units', headerKey: 'inseason.column.onHand', descriptionKey: 'inseason.columnGuide.stockPosition.onHand', kind: 'number', unit: 'pairs' },
      { key: 'committed_units', headerKey: 'inseason.column.committed', descriptionKey: 'inseason.columnGuide.stockPosition.committed', kind: 'number', unit: 'pairs' },
      { key: 'weekly_demand', headerKey: 'inseason.column.weeklyDemand', descriptionKey: 'inseason.columnGuide.stockPosition.weeklyDemand', kind: 'number', unit: 'pairs' },
      { key: 'weeks_of_cover', headerKey: 'inseason.column.cover', descriptionKey: 'inseason.columnGuide.stockPosition.cover', kind: 'number', unit: 'weeks' },
      { key: 'bound_by', headerKey: 'inseason.column.boundBy', descriptionKey: 'inseason.columnGuide.stockPosition.boundBy', kind: 'label', display: BOUND_BY_WORDS },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, [
      {
        measure: 'stock_position',
        definition:
          'Store-level stock position for the run: on hand, committed, weekly demand and weeks of cover per store, article and size. Distribution-centre stock is outside the perimeter. Every measure is nullable and an absence is never a zero.',
      },
    ]),
    perimeter: {
      filters,
      sort: [],
      visibleColumns: [
        'store_key', 'article_key', ...(names === null ? [] : nameExportColumns(['store', 'article']).map((column) => column.key)),
        ...SIZE_EXPORT_KEYS, 'position_state', 'on_hand_units', 'committed_units', 'weekly_demand', 'weeks_of_cover', 'bound_by'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

// ── the allocation drill (bata-ui#148 step 4.4, docs/adr/6006) ───────────────

/** The store and article a drill path names, as provenance filters beside the run. */
function drillFilters(runId: string | null, storeKey: string | null, articleKey: string | null): readonly ProvenanceFilter[] {
  return [
    ...runFilter(runId),
    ...(storeKey === null ? [] : [{ field: 'store_key', operator: 'equals', value: storeKey }]),
    ...(articleKey === null ? [] : [{ field: 'article_key', operator: 'equals', value: articleKey }]),
  ];
}

/**
 * One drill level's proposal lines — the proposal sheet, re-titled and re-scoped. The rows are
 * the ones the page drew (a `DataTable` hands its view in) or, for the route's export, the
 * run's lines for the store (and article) the path names; the filters say which.
 */
export async function drillLinesRequest(
  items: readonly ProposalSummary[],
  runId: string | null,
  storeKey: string | null,
  articleKey: string | null,
): Promise<ExportRequest> {
  const base = await proposalRequest(items, runId);
  // The page's perimeter, and the dropped doors' exclusion the proposal file already stated.
  const filters = [...drillFilters(runId, storeKey, articleKey), ...exclusionFiltersOf(base)];
  const drillPath = [storeKey, articleKey].flatMap((key) => (key === null ? [] : [key]));
  const titled = retitled(
    base,
    articleKey === null ? 'inseason.store.table' : 'inseason.storeArticle.table',
    articleKey === null ? 'inseason.store.linesTitle' : 'inseason.storeArticle.sizesTitle',
  );
  return {
    ...titled,
    provenance: { ...base.provenance, filters },
    perimeter: { ...base.perimeter, filters, drillPath },
  };
}

/**
 * A drill level's stock positions — the positions sheet, scoped to the store (and article), with
 * the store and article names the page shows beside them (usability audit B3): the run's
 * replenishment report, and the network-health page of the store's articles for an article with
 * positions and no line — the same reads `StorePositions` names its rows from.
 */
export async function drillPositionsRequest(
  items: readonly StockPositionSummary[],
  runId: string | null,
  storeKey: string | null,
  articleKey: string | null,
): Promise<ExportRequest> {
  const names = await readRunNames(runId, { replenishment: true }, storeKey === null ? null : { groupBy: 'article', storeKey });
  const base = stockPositionsRequest(items, runId, items.length, names);
  const filters = drillFilters(runId, storeKey, articleKey);
  return {
    ...base,
    id: articleKey === null ? 'inseason.store.positions' : 'inseason.storeArticle.positions',
    titleKey: 'inseason.store.positionsTitle',
    provenance: { ...base.provenance, filters },
    perimeter: { ...base.perimeter, filters, drillPath: [storeKey, articleKey].flatMap((key) => (key === null ? [] : [key])) },
  };
}

async function drillExport(given: ExportScope | undefined, level: 'store' | 'article'): Promise<ExportRequest> {
  const { runId } = scope(given);
  const pathname = given?.path ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const drill = readDrill(pathname);
  const storeKey = drill.storeKey;
  const articleKey = level === 'article' ? drill.articleKey : null;
  const unbounded = runId === null || storeKey === null || (level === 'article' && articleKey === null);
  const items = unbounded
    ? []
    : await collectPages(inseasonListProposals, {
      query: { run_id: runId, store_key: storeKey, ...(articleKey === null ? {} : { article_key: articleKey }) },
    });
  return drillLinesRequest(items, runId, storeKey, articleKey);
}

/** `/inseason/allocation/:storeKey` — the store's lines in the run. */
export async function storeLinesExport(given?: ExportScope): Promise<ExportRequest> {
  return drillExport(given, 'store');
}

/** `/inseason/allocation/:storeKey/:articleKey` — the article's sizes in the store. */
export async function storeArticleExport(given?: ExportScope): Promise<ExportRequest> {
  return drillExport(given, 'article');
}

// ── the store × article sales history (bata-ui#166 W4-3, docs/adr/6101) ──────

/**
 * The sales-history table as drawn: one row per ISO week (and size, when split). An unlanded
 * week exports as `not-reported`, never 0 — the same absence the table draws. Net value is
 * one column per currency the view carries, because a sum across currencies is not a number.
 */
export function salesHistoryRequest(
  weeks: readonly SalesHistoryWeek[],
  storeKey: string,
  articleKey: string,
  window: { readonly from: string; readonly to: string; readonly bySize: boolean },
  statement?: WindowStatement,
): ExportRequest {
  const currencies = [...new Set(weeks.flatMap((week) => week.net_value.map((money) => money.currency_code)))].sort();
  const units = (value: number | null): ExportCell => (
    value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'integer', raw: value }
  );
  const rows: ExportCell[][] = weeks.map((week) => [
    { kind: 'date', iso: week.week_start },
    { kind: 'integer', raw: week.iso_year },
    { kind: 'integer', raw: week.iso_week },
    ...(window.bySize
      ? [
        { kind: 'text', value: week.size_scale ?? '' } as const,
        { kind: 'text', value: week.size_code ?? '' } as const,
      ]
      : []),
    { kind: 'label', value: week.sales_coverage, code: week.sales_coverage },
    units(week.sold_units),
    ...currencies.map((currency): ExportCell => {
      const money = week.net_value.find((entry) => entry.currency_code === currency);
      if (money !== undefined) return { kind: 'money', raw: Number(money.amount), currency };
      return week.sold_units === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'money', raw: 0, currency };
    }),
    { kind: 'label', value: week.returns_coverage, code: week.returns_coverage },
    units(week.returned_units),
  ]);
  const filters: readonly ProvenanceFilter[] = [
    { field: 'store_key', operator: 'equals', value: storeKey },
    { field: 'article_key', operator: 'equals', value: articleKey },
    { field: 'from', operator: 'equals', value: window.from },
    { field: 'to', operator: 'equals', value: window.to },
    { field: 'by', operator: 'equals', value: window.bySize ? 'size' : 'total' },
    // Whose window it is (ADR 6120): the planner's, or the platform's governed default with the
    // weeks and the change set that set it. Stated, so an export never passes one for the other.
    ...(statement === undefined ? [] : [{ field: 'window_source', operator: 'equals', value: statement.source } as const]),
    ...(statement?.values['weeks'] === undefined ? [] : [{ field: 'window_weeks', operator: 'equals', value: String(statement.values['weeks']) } as const]),
    ...(statement?.values['changeSet'] === undefined ? [] : [{ field: 'window_parameter_set_id', operator: 'equals', value: String(statement.values['changeSet']) } as const]),
  ];
  const columns: ExportColumn[] = [
    { key: 'week_start', headerKey: 'inseason.salesHistory.column.weekStart', descriptionKey: 'inseason.columnGuide.salesHistory.weekStart', kind: 'date' },
    { key: 'iso_year', headerKey: 'inseason.salesHistory.column.isoYear', descriptionKey: 'inseason.columnGuide.salesHistory.isoYear', kind: 'integer' },
    { key: 'iso_week', headerKey: 'inseason.salesHistory.column.isoWeek', descriptionKey: 'inseason.columnGuide.salesHistory.isoWeek', kind: 'integer' },
    ...(window.bySize
      ? [
        { key: 'size_scale', headerKey: 'inseason.salesHistory.column.sizeScale', descriptionKey: 'inseason.columnGuide.salesHistory.sizeScale', kind: 'text' } as const,
        { key: 'size_code', headerKey: 'inseason.column.size', descriptionKey: 'inseason.columnGuide.salesHistory.sizeCode', kind: 'text' } as const,
      ]
      : []),
    { key: 'sales_coverage', headerKey: 'inseason.salesHistory.column.salesCoverage', descriptionKey: 'inseason.columnGuide.salesHistory.salesCoverage', kind: 'label' },
    { key: 'sold_units', headerKey: 'inseason.salesHistory.column.sold', descriptionKey: 'inseason.columnGuide.salesHistory.sold', kind: 'integer', unit: 'pairs' },
    ...currencies.map((currency): ExportColumn => ({
      key: ['net_value', currency].join('_'), headerKey: 'inseason.salesHistory.column.netValue', descriptionKey: 'inseason.columnGuide.salesHistory.netValue', kind: 'money', currency,
    })),
    { key: 'returns_coverage', headerKey: 'inseason.salesHistory.column.returnsCoverage', descriptionKey: 'inseason.columnGuide.salesHistory.returnsCoverage', kind: 'label' },
    { key: 'returned_units', headerKey: 'inseason.salesHistory.column.returned', descriptionKey: 'inseason.columnGuide.salesHistory.returned', kind: 'integer', unit: 'pairs' },
  ];
  return {
    id: 'inseason.storeArticle.salesHistory',
    titleKey: 'inseason.salesHistory.title',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: {
      ...provenance(filters, [
        {
          measure: 'sales_history',
          definition:
            'Units sold, net sales value and units returned for one store and one article per ISO week (Monday start), from the promoted, unsuperseded sales and returns batches landed in the plan store. A week no batch covers is not reported, never zero; a partly covered week sums the covered days and is marked partial.',
        },
      ]),
      timeWindow: { from: window.from, to: window.to },
    },
    perimeter: {
      filters,
      sort: [],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [storeKey, articleKey],
      densityMode: 'compact',
    },
  };
}

/**
 * One department family's proposal lines in a run — `/inseason/departments/:departmentFamily`
 * (ADR 6102). The rows the page drew, or for the route's export the run's lines the platform's
 * `department_family` filter returns; the filters say which family and which run.
 */
export async function departmentLinesRequest(
  items: readonly ProposalSummary[],
  runId: string | null,
  departmentFamily: string | null,
): Promise<ExportRequest> {
  const base = await proposalRequest(items, runId);
  const filters: readonly ProvenanceFilter[] = [
    ...runFilter(runId),
    ...(departmentFamily === null ? [] : [{ field: 'department_family', operator: 'equals' as const, value: departmentFamily }]),
    ...exclusionFiltersOf(base),
  ];
  return {
    ...retitled(base, 'inseason.department.table', 'inseason.department.linesTitle'),
    provenance: { ...base.provenance, filters },
    perimeter: { ...base.perimeter, filters, drillPath: departmentFamily === null ? [] : [departmentFamily] },
  };
}

/** `/inseason/departments/:departmentFamily` — the family's lines in the run. */
export async function departmentLinesExport(given?: ExportScope): Promise<ExportRequest> {
  const { runId } = scope(given);
  const pathname = given?.path ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const family = readDepartment(pathname);
  // The page narrowed to the served lines (`?lines=served`, ADR 9069) exports the lines it lists.
  const served = readLinesNarrowing(search) === LINES_SERVED;
  const items = runId === null || family === null
    ? []
    : await collectPages(inseasonListProposals, { query: { run_id: runId, department_family: family, ...(served ? { lines: LINES_SERVED } : {}) } });
  return departmentLinesRequest(items, runId, family);
}

// ── the drill's own sub-pages (client rule 3, DEC-88, ADR 6006 amendment 2026-09-30) ──────────

/**
 * `/inseason/allocation/:storeKey/:articleKey/sales-history` — the store × article sales history,
 * over the window the address states (or the platform's governed one, with none), exactly as the
 * page's table exports it (`salesHistoryRequest`). An invalid window or an unnamed level
 * exports no rows rather than a read the page would not have made.
 */
export async function salesHistoryExport(given?: ExportScope): Promise<ExportRequest> {
  const pathname = given?.path ?? (typeof window === 'undefined' ? '' : window.location.pathname);
  const search = given?.search ?? (typeof window === 'undefined' ? '' : window.location.search);
  const { storeKey, articleKey } = readDrill(pathname);
  const window_ = readSalesWindow(search);
  const store = storeKey ?? '';
  const article = articleKey ?? '';
  if (storeKey === null || articleKey === null || window_.state === 'invalid') {
    return salesHistoryRequest([], store, article, { from: '', to: '', bySize: false });
  }
  const history = await callOperation(salesHistory, { query: salesHistoryQuery(storeKey, articleKey, window_) }, {});
  return salesHistoryRequest(
    history.weeks, storeKey, articleKey,
    { from: history.window_start, to: history.window_end, bySize: window_.bySize },
    windowStatement(history),
  );
}

/**
 * `/inseason/run-provenance` — every registry value the run resolved (bata-platform ADR 6121),
 * the table the page draws for the run's own half: key, value, scope, change set and in force
 * from, each absence with the platform's reason code. Bounded by `?runId=`; with none, no read.
 */
export async function runProvenanceExport(given?: ExportScope): Promise<ExportRequest> {
  const { runId } = scope(given);
  const parameters = runId === null
    ? []
    : (await callOperation(runProvenance, { path: { run_id: runId } }, {})).run.parameters;
  const text = (value: string | null): ExportCell => (
    value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value }
  );
  const rows: ExportCell[][] = parameters.map((row) => [
    { kind: 'text', value: row.key },
    row.numeric_value === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'number', raw: row.numeric_value },
    text(row.text_value),
    { kind: 'text', value: row.scope_dimension },
    { kind: 'text', value: row.scope_value },
    text(row.change_set_id),
    row.valid_from === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'date', iso: row.valid_from },
    text(row.absent_reason),
  ]);
  const columns: ExportColumn[] = [
    { key: 'key', headerKey: 'inseason.provenance.column.key', descriptionKey: 'inseason.columnGuide.runParameters.key', kind: 'text' },
    { key: 'numeric_value', headerKey: 'inseason.provenance.column.value', descriptionKey: 'inseason.columnGuide.runParameters.numericValue', kind: 'number' },
    { key: 'text_value', headerKey: 'inseason.provenance.column.value', descriptionKey: 'inseason.columnGuide.runParameters.textValue', kind: 'text' },
    { key: 'scope_dimension', headerKey: 'inseason.provenance.column.scope', descriptionKey: 'inseason.columnGuide.runParameters.scopeDimension', kind: 'text' },
    { key: 'scope_value', headerKey: 'inseason.provenance.column.scope', descriptionKey: 'inseason.columnGuide.runParameters.scopeValue', kind: 'text' },
    { key: 'change_set_id', headerKey: 'inseason.provenance.column.changeSet', descriptionKey: 'inseason.columnGuide.runParameters.changeSet', kind: 'text' },
    { key: 'valid_from', headerKey: 'inseason.provenance.column.validFrom', descriptionKey: 'inseason.columnGuide.runParameters.validFrom', kind: 'date' },
    { key: 'absent_reason', headerKey: 'inseason.provenance.column.absentReason', descriptionKey: 'inseason.columnGuide.runParameters.absentReason', kind: 'text' },
  ];
  const filters = runFilter(runId);
  return {
    id: 'inseason.provenance.runParameters',
    titleKey: 'inseason.provenance.title',
    columns,
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance(filters, [
      {
        measure: 'run_parameters',
        definition:
          'Every registry value the run resolved when it ran, at the scope and from the change set it resolved it at (bata-platform ADR 6121). Never today’s registry: a run that did not record it exports no rows.',
      },
    ]),
    perimeter: {
      filters,
      sort: [{ key: 'key', direction: 'asc' }],
      visibleColumns: columns.map((column) => column.key),
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
